import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import printDocument from '../utils/print';
import { ListFooter } from '../ui';

// ── Shared constants ──────────────────────────────────────────────────────────
export const SB_CATEGORY_OPTIONS = ['Men Upper', 'Ladies Upper', 'Men Lower', 'Ladies Lower', 'Jr Kids', 'Sr Kids'];
const SIZE_SETS = {
  upper: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'],
  bottom: ['28', '30', '32', '34', '36', '38', '40'],
  jr_kids: ['2-4', '4-6', '6-8', '8-10'],
  sr_kids: ['8-10', '10-12', '12-14', '14-16'],
};
export const sbSizesFor = (division) => {
  const c = (division || '').toLowerCase();
  if (c.includes('jr kids')) return SIZE_SETS.jr_kids;
  if (c.includes('sr kids')) return SIZE_SETS.sr_kids;
  if (c.includes('lower')) return SIZE_SETS.bottom;
  if (c.includes('upper')) return SIZE_SETS.upper;
  return [];
};
const MACHINE_TYPES = ['SNL', 'OL', 'FS', 'BH', 'BT', 'FOA', 'Manual'];
const MACHINE_LEGEND = 'SNL=Single Needle Lock Stitch · OL=Overlock · FS=Flat Seam · BH=Buttonhole · BT=Bartack · FOA=Feed-off-the-Arm';
const SAMPLE_TYPES = ['1st', '2nd', 'PP', 'SMS'];
const LAYER_PLAN_OPTS = ['Attached', 'To be provided by IE', 'Not applicable'];
const PACKING_METHODS = ['Flat Pack', 'Hanger', 'Roll Pack', 'As per buyer SOP'];
const ATTACHMENT_ITEMS = [
  ['tech_pack', 'Tech Pack'], ['approved_sample', 'Approved Sample'], ['layer_plan', 'Layer Plan'],
  ['measurement_chart', 'Measurement Chart'], ['fit_comment', 'Fit Comment Sheet'], ['fabric_swatch', 'Fabric Swatch'],
  ['lab_dip', 'Lab Dip Approval'], ['trim_card', 'Trim Card'], ['aql_sheet', 'AQL Sheet'],
  ['cost_estimation', 'Khyati Portal Cost Estimation Sheet'],
];
export const SB_STATUS_FLOW = ['draft', 'issued', 'sample_making', 'sample_ready_tms', 'tms_in_progress', 'pending_pm_approval', 'tms_done', 'sample_ready_merchant', 'handed_to_merchant', 'sample_accepted'];
// Once PM-approved (tms_done onward) the T&M/SAM is final and feeds Cost Estimation:
// the bulletin is read-only for everyone except an admin using the amendment path.
export const SB_LOCKED_STATUSES = ['tms_done', 'sample_ready', 'sample_ready_merchant', 'handed_to_merchant', 'sample_accepted'];
export const sbIsLocked = (status) => SB_LOCKED_STATUSES.includes(status);
export const SB_STATUS_LABEL = {
  draft: 'Draft', kitting: 'Kitting', issued: 'Issued to Sampling Room',
  sample_making: 'Sample Making In Progress', sample_ready_tms: 'Sample Ready for T&M',
  tms_in_progress: 'T&M In Progress', pending_pm_approval: 'Pending Production Approval', tms_done: 'T&M Done · Approved',
  sample_ready: 'Sample Ready', // legacy alias
  sample_ready_merchant: 'Sample Ready for Merchant', handed_to_merchant: 'Handed to Merchant',
  sample_accepted: 'Sample Accepted',
};
// Ordinal sample label: revision_no 1 → "1st sample"
export const sampleOrdinal = (n) => { const r = Number(n) || 1; const s = ['th', 'st', 'nd', 'rd'], v = r % 100; return r + (s[(v - 20) % 10] || s[v] || s[0]) + ' sample'; };

const inp = { width: '100%', padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const lbl = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const sectionHd = { fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px', paddingBottom: 6, borderBottom: '2px solid #EEEDF8' };
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const emptyFabric = () => ({ fabric_desc: '', composition: '', gsm: '', dia_width: '', technique: 'Knit', shade: '', unit: 'KG', cpp: '' });
const emptyTrim = () => ({ item_name: '', category: 'Label', spec: '', unit: 'PC', cpp: '', supplier: '' });
const emptyOp = () => ({ operation_name: '', machine_type: 'SNL', part: '', est_sam: '', remarks: '', image_url: '', image_name: '' });

const IMG_TYPES = 'image/jpeg,image/png';
const CHART_TYPES = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/jpeg,image/png';
const GARMENT_TYPES = 'image/jpeg,image/png,application/pdf';
const isImageName = (n) => /\.(jpe?g|png|gif|webp)$/i.test(n || '');

// SAM sanity band (mirror of server samWarnings in sample-bulletins.routes.js).
// Our own bulletins sit at ~10–40 min; a total far below that almost always means
// observed times were keyed in MINUTES instead of SECONDS. Also flags any op under
// 3 observed seconds. WARN only — the caller confirms but never blocks the save.
const SAM_MIN_MIN = 10, SAM_MAX_MIN = 40, MIN_OBS_SEC = 3;
const r2 = (n) => Math.round(n * 100) / 100;
function samWarnings(ops, total) {
  const list = Array.isArray(ops) ? ops : [];
  const timed = list.filter((o) => { const t = parseFloat(o?.observed_time); return Number.isFinite(t) && t > 0; });
  const out = [];
  if (!timed.length) return out;
  const t = Number.isFinite(parseFloat(total)) ? parseFloat(total) : 0;
  if (t > 0 && t < SAM_MIN_MIN) out.push(`Total SAM ${r2(t)} min is far below the normal ${SAM_MIN_MIN}–${SAM_MAX_MIN} min range for a garment. Check your observed times are in SECONDS, not minutes.`);
  else if (t > SAM_MAX_MIN) out.push(`Total SAM ${r2(t)} min is above the normal ${SAM_MIN_MIN}–${SAM_MAX_MIN} min range for a garment. Double-check the observed times.`);
  const tiny = timed.filter((o) => parseFloat(o.observed_time) < MIN_OBS_SEC).map((o) => o.operation_name || '(unnamed)');
  if (tiny.length) out.push(`${tiny.length} operation(s) have an observed time under ${MIN_OBS_SEC} seconds (${tiny.slice(0, 5).join(', ')}${tiny.length > 5 ? '…' : ''}) — nothing on a garment takes under ${MIN_OBS_SEC}s. Likely entered in minutes.`);
  return out;
}
const confirmSam = (ops, total) => {
  const w = samWarnings(ops, total);
  return !w.length || window.confirm('⚠ SAM sanity check\n\n' + w.join('\n\n') + '\n\nSave anyway?');
};

// Section wrapper — defined at MODULE scope (not inside the form component) so its
// identity is stable across renders. Defining it inline caused React to remount the
// whole subtree on every keystroke, making inputs lose focus after one character.
const Section = ({ n, title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h4 style={sectionHd}>{n}. {title}</h4>
    {children}
  </div>
);

// Reusable upload button — posts to the shared /files/upload endpoint and returns
// { url, name } (or an array when multiple). Module-scope for stable identity.
function Uploader({ accept, multiple, onUploaded, label, style }) {
  const ref = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const metas = [];
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        metas.push({ url: r.data.url || r.data.file?.url, name: r.data.file?.originalname || file.name });
      }
      onUploaded(multiple ? metas : metas[0]);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }} onChange={onChange} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        style={{ padding: '6px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', borderRadius: 6, fontSize: 12, cursor: busy ? 'wait' : 'pointer', fontWeight: 600, ...style }}>
        {busy ? 'Uploading…' : label}
      </button>
    </>
  );
}

// Searchable dropdown backed by a master list. Shows a filterable popup; picking an
// item calls onPick(item). `value` is the currently-selected display text.
function MasterSelect({ items, value, onPick, placeholder, labelOf, subOf }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const filtered = (items || []).filter(it => !ql || labelOf(it).toLowerCase().includes(ql) || (subOf && (subOf(it) || '').toLowerCase().includes(ql)));
  return (
    <div style={{ position: 'relative' }}>
      <input style={inp} placeholder={placeholder || 'Search…'} value={open ? q : (value || '')}
        onFocus={() => { setOpen(true); setQ(''); }}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && (
        <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 6, maxHeight: 210, overflowY: 'auto', boxShadow: '0 6px 20px rgba(0,0,0,0.14)' }}>
          {filtered.length === 0 ? <div style={{ padding: '8px 10px', fontSize: 11, color: '#aaa' }}>No match</div>
            : filtered.slice(0, 60).map(it => (
              <div key={it.id} onMouseDown={() => { onPick(it); setOpen(false); }}
                style={{ padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F7F6F3'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <div style={{ fontWeight: 600, color: '#333' }}>{labelOf(it)}</div>
                {subOf && <div style={{ fontSize: 10, color: '#888' }}>{subOf(it)}</div>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// Compact display of an uploaded file: name + view link (+ thumbnail for images).
const FileChip = ({ file, onRemove, thumb }) => {
  if (!file || !file.url) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 11 }}>
      {thumb && isImageName(file.name || file.url) && (
        <a href={file.url} target="_blank" rel="noreferrer"><img src={file.url} alt={file.name} style={{ height: 40, width: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} /></a>
      )}
      <a href={file.url} target="_blank" rel="noreferrer" style={{ color: '#3B3A8F', fontWeight: 600 }}>📄 {file.name || 'View file'}</a>
      {onRemove && <button type="button" onClick={onRemove} style={{ border: 'none', background: 'none', color: '#E22D2D', cursor: 'pointer', fontSize: 13 }}>✕</button>}
    </div>
  );
};

// ── Status pipeline (per row) ─────────────────────────────────────────────────
function StatusPipeline({ status }) {
  const idx = SB_STATUS_FLOW.indexOf(status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {SB_STATUS_FLOW.filter(s => s !== 'kitting').map((s, i) => {
        const realIdx = SB_STATUS_FLOW.indexOf(s);
        const done = realIdx <= idx;
        return (
          <span key={s} title={SB_STATUS_LABEL[s]} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 18 }}>
            <span style={{ fontSize: 9 }}>{done ? '🟢' : '⚪'}</span>
          </span>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sample Bulletin Form (9 sections)
// ════════════════════════════════════════════════════════════════════════════
function SampleBulletinForm({ bulletin, onClose, onSaved, user }) {
  const [ieOfficers, setIeOfficers] = useState([]);
  const [form, setForm] = useState(() => {
    if (bulletin) {
      const j = (v, d) => { try { return typeof v === 'string' ? JSON.parse(v) : (v || d); } catch { return d; } };
      return {
        ...bulletin,
        fabrics_json: j(bulletin.fabrics_json, [emptyFabric()]),
        trims_json: j(bulletin.trims_json, [emptyTrim()]),
        operations_json: j(bulletin.operations_json, [emptyOp()]),
        measurement_points: j(bulletin.measurement_points, []),
        size_ratio: j(bulletin.size_ratio, {}),
        attachments_json: j(bulletin.attachments_json, {}),
        revision_history: j(bulletin.revision_history, []),
        media_json: j(bulletin.media_json, {}),
      };
    }
    return {
      sample_type: '1st', division: '', fabrics_json: [emptyFabric()], trims_json: [emptyTrim()],
      operations_json: [emptyOp()], measurement_points: [], size_ratio: {}, attachments_json: {},
      revision_history: [], media_json: {}, layer_plan_status: 'To be provided by IE', packing_method: 'Flat Pack',
      embroidery_print: 'None', issue_date: '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fabricMaster, setFabricMaster] = useState([]);
  const [trimsMaster, setTrimsMaster] = useState([]);
  const [noStatus, setNoStatus] = useState('loading'); // loading | ok | fail (Bulletin No preview)

  useEffect(() => { api.get('/sample-bulletins/ie-officers').then(r => setIeOfficers(r.data.data || [])).catch(() => {}); }, []);

  // Master data for the searchable Fabric / Trims dropdowns.
  useEffect(() => {
    api.get('/oe/fabric-master').then(r => setFabricMaster(r.data.data || [])).catch(() => {});
    api.get('/oe/trims-master').then(r => setTrimsMaster(r.data.data || [])).catch(() => {});
  }, []);

  // Auto-generate the Bulletin No on open for a brand-new bulletin (preview from the
  // server sequence; the final number is assigned atomically on save). On failure we
  // fall back to a clear label instead of hanging on "Generating…".
  useEffect(() => {
    if (!form.id && !form.bulletin_no) {
      let settled = false;
      // Hard stop: never let the field hang on "Generating…" — if the request stalls
      // (slow/hung network), fall back to a clear label after 8s.
      const timer = setTimeout(() => { if (!settled) { settled = true; setNoStatus('fail'); } }, 8000);
      api.get('/sample-bulletins/next-no')
        .then(r => {
          if (settled) return; settled = true; clearTimeout(timer);
          const no = r.data?.data?.bulletin_no;
          // Only mark "ok" when we actually got a number — otherwise fall back to a
          // clear label instead of hanging forever on "Generating…".
          if (no) { setForm(p => (p.bulletin_no ? p : { ...p, bulletin_no: no })); setNoStatus('ok'); }
          else { setNoStatus('fail'); }
        })
        .catch(() => { if (settled) return; settled = true; clearTimeout(timer); setNoStatus('fail'); });
    } else { setNoStatus('ok'); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setMedia = (k, v) => setForm(p => ({ ...p, media_json: { ...(p.media_json || {}), [k]: v } }));
  const totalEstSam = (form.operations_json || []).reduce((s, o) => s + (parseFloat(o.est_sam) || 0), 0);
  // A bulletin is editable as a draft only before it's issued to the Sampling Room.
  // Once issued/in-study/done/approved it's read-only here (the IE owns the study).
  const isDraft = !form.status || form.status === 'draft';
  // Per-op SAM: studied value (`sam`, set by the T&M study) wins, else the estimate.
  const samOf = (o) => (o.sam !== undefined && o.sam !== null && o.sam !== '') ? o.sam : o.est_sam;
  const studyDone = (form.operations_json || []).some(o => o.sam !== undefined && o.sam !== null && o.sam !== '');
  const totalSamShown = form.total_sam_actual != null ? Number(form.total_sam_actual)
    : (form.operations_json || []).reduce((s, o) => s + (parseFloat(samOf(o)) || 0), 0);

  const setRow = (key, i, patch) => f(key, (form[key] || []).map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRow = (key, factory, max) => { if ((form[key] || []).length < (max || 99)) f(key, [...(form[key] || []), factory()]); };
  const delRow = (key, i) => f(key, (form[key] || []).filter((_, j) => j !== i));

  // The preview number is display-only; let the server assign the real one on create.
  const payload = () => { const p = { ...form, total_sam_estimated: totalEstSam }; if (!form.id) delete p.bulletin_no; return p; };
  const bulletinNoDisplay = form.bulletin_no || (noStatus === 'fail' ? 'Will be assigned on save' : 'Generating…');
  const construction_images = (form.media_json || {}).construction_images || [];

  const save = async (statusAfter) => {
    setSaving(true); setError('');
    try {
      let saved;
      if (form.id) { saved = (await api.patch('/sample-bulletins/' + form.id, payload())).data.data; }
      else { saved = (await api.post('/sample-bulletins', payload())).data.data; f('id', saved.id); f('bulletin_no', saved.bulletin_no); }
      if (statusAfter === 'issue') {
        await api.post('/sample-bulletins/' + saved.id + '/issue');
      }
      onSaved();
      onClose();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save bulletin'); }
    finally { setSaving(false); }
  };

  const sign = async (role) => {
    if (!form.id) { setError('Save the bulletin first'); return; }
    try { const r = await api.post('/sample-bulletins/' + form.id + '/sign', { role }); setForm(p => ({ ...p, ...r.data.data })); } catch (e) { setError(e.response?.data?.error || 'Could not sign — the server did not accept it.'); }
  };

  const doPrint = () => {
    printDocument({
      docTitle: 'Sample Bulletin', subtitle: form.bulletin_no || '(draft)',
      meta: [
        ['Style No', form.style_no], ['Buyer / Brand', [form.buyer, form.brand].filter(Boolean).join(' / ')],
        ['Season', form.season], ['Garment', form.garment_description], ['Division', form.division],
        ['Sample Type', form.sample_type], ['Order Qty', form.order_qty], ['Sample Qty', form.sample_qty],
        ['Delivery', fmt(form.delivery_date)], ['Issued To', form.issued_to], ['Issue Date', fmt(form.issue_date)],
        [studyDone ? 'Total Actual SAM' : 'Total Est SAM', totalSamShown.toFixed(3) + ' min'], ['Status', SB_STATUS_LABEL[form.status] || 'Draft'],
      ].filter(m => m[1]),
      sections: [
        { title: 'Fabric Details', table: { columns: ['Fabric', 'Composition', 'GSM', 'DIA', 'Shade', 'CPP'], rows: (form.fabrics_json || []).filter(x => x.fabric_desc).map(x => [x.fabric_desc, x.composition, x.gsm, x.dia_width, x.shade, `${x.cpp || ''} ${x.unit || ''}`]) } },
        { title: 'Trims & Accessories', table: { columns: ['Item', 'Category', 'Spec', 'Unit', 'CPP', 'Supplier'], rows: (form.trims_json || []).filter(x => x.item_name).map(x => [x.item_name, x.category, x.spec, x.unit, x.cpp, x.supplier]) } },
        { title: 'Operation Breakdown', table: { columns: ['#', 'Operation', 'M/C', 'Part', studyDone ? 'SAM' : 'Est SAM', 'Remarks'], rows: (form.operations_json || []).filter(x => x.operation_name).map((x, i) => [i + 1, x.operation_name, x.machine_type, x.part, samOf(x), x.remarks]) } },
        { title: 'Construction', fields: [['Seam / SPI', `${form.seam_type || '—'} / ${form.spi || '—'}`], ['Thread', `${form.thread_type || '—'} ${form.thread_colour || ''}`], ['Collar/Cuff/Placket/Hem', [form.collar_type, form.cuff_type, form.placket_type, form.hem_type].filter(Boolean).join(' · ')], ['Embroidery/Print', `${form.embroidery_print} ${form.embroidery_placement || ''} ${form.print_type || ''}`]] },
        { title: 'Special Instructions', fields: [['Washing', form.washing_instructions], ['Packing', form.packing_method], ['Critical QC', form.critical_qc_points], ['Notes', form.additional_notes]].filter(r => r[1]) },
      ],
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '20px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '95%', maxWidth: 1180, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 5, borderRadius: '12px 12px 0 0' }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: 16, color: 'rgba(255,255,255,0.72)' }}>📋 Sample Bulletin {form.bulletin_no ? `— ${form.bulletin_no}` : '(new)'}</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.72)' }}>Kitting document · {SB_STATUS_LABEL[form.status] || 'Draft'}</p>
            {form.copied_from_bulletin_no && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#FDE68A', fontWeight: 600 }}>📋 Copied from {form.copied_from_bulletin_no}{form._copied_from_date ? ` · ${fmt(form._copied_from_date)}` : ''} — operations copied as-is; enter the new style no &amp; adjust processes.</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'rgba(255,255,255,0.85)', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px', maxHeight: '78vh', overflowY: 'auto' }}>
          {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}

          {/* Section 1 — Order & Style */}
          <Section n={1} title="Order & Style">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              <div><label style={lbl}>Bulletin No</label><input style={{ ...inp, background: '#F7F6F3', fontWeight: 700, color: form.bulletin_no ? '#3B3A8F' : '#999' }} value={bulletinNoDisplay} readOnly /></div>
              <div><label style={lbl}>Style No *</label><input style={inp} value={form.style_no || ''} onChange={e => f('style_no', e.target.value)} /></div>
              <div><label style={lbl}>Buyer</label><input style={inp} value={form.buyer || ''} onChange={e => f('buyer', e.target.value)} /></div>
              <div><label style={lbl}>Brand</label><input style={inp} value={form.brand || ''} onChange={e => f('brand', e.target.value)} /></div>
              <div><label style={lbl}>Season</label><input style={inp} value={form.season || ''} onChange={e => f('season', e.target.value)} /></div>
              <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Garment Description</label><input style={inp} value={form.garment_description || ''} onChange={e => f('garment_description', e.target.value)} placeholder="e.g. KNITTED POLO SHIRT" /></div>
              <div><label style={lbl}>Division</label>
                <select style={inp} value={form.division || ''} onChange={e => f('division', e.target.value)}>
                  <option value="">Select</option>{SB_CATEGORY_OPTIONS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Sample Type</label>
                <select style={inp} value={form.sample_type || '1st'} onChange={e => f('sample_type', e.target.value)}>{SAMPLE_TYPES.map(s => <option key={s}>{s}</option>)}</select>
              </div>
              <div><label style={lbl}>Order Qty</label><input style={inp} type="number" value={form.order_qty || ''} onChange={e => f('order_qty', e.target.value)} /></div>
              <div><label style={lbl}>Sample Qty</label><input style={inp} type="number" value={form.sample_qty || ''} onChange={e => f('sample_qty', e.target.value)} /></div>
              <div><label style={lbl}>Delivery Date</label><input style={inp} type="date" value={form.delivery_date ? String(form.delivery_date).slice(0, 10) : ''} onChange={e => f('delivery_date', e.target.value)} /></div>
              <div><label style={lbl}>Merchandiser</label><input style={{ ...inp, background: '#F7F6F3' }} value={form.merchandiser_name || user?.name || ''} readOnly /></div>
              <div><label style={lbl}>Issued To (IE)</label>
                <select style={inp} value={form.issued_to || ''} onChange={e => f('issued_to', e.target.value)}>
                  <option value="">Select IE officer</option>{ieOfficers.map(o => <option key={o.id} value={o.name}>{o.name} ({o.role})</option>)}
                </select>
              </div>
              <div><label style={lbl}>Issue Date</label><input style={inp} type="date" value={form.issue_date ? String(form.issue_date).slice(0, 10) : ''} onChange={e => f('issue_date', e.target.value)} /></div>
            </div>
          </Section>

          {/* Section 2 — Garment Reference Image */}
          <Section n={2} title="Garment Reference Image">
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Upload garment image / tech pack sketch (JPG, PNG, PDF).</div>
            <Uploader accept={GARMENT_TYPES} label="📷 Upload Garment Image"
              onUploaded={m => setMedia('garment_image', m)} />
            {(form.media_json || {}).garment_image && (
              <FileChip thumb file={(form.media_json || {}).garment_image} onRemove={() => setMedia('garment_image', null)} />
            )}
          </Section>

          {/* Section 3 — Fabric Details (from Fabric Master) */}
          <Section n={3} title="Fabric Details">
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Search & select a fabric from the master — name, composition, GSM, DIA & unit auto-fill. Enter only Colour/Shade and CPP/Qty.</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Fabric (search master)', 'Composition', 'GSM', 'DIA/Width', 'Technique', 'Unit', 'Colour/Shade *', 'CPP/Qty *', ''].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#888', fontSize: 10, borderBottom: '1px solid #eee' }}>{h}</th>)}</tr></thead>
              <tbody>
                {(form.fabrics_json || []).map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: 3, minWidth: 180 }}>
                      <MasterSelect items={fabricMaster} value={row.fabric_desc} placeholder="Search fabric…"
                        labelOf={it => it.fabric_name}
                        subOf={it => [it.composition, it.gsm && `${it.gsm} GSM`, it.dia_width].filter(Boolean).join(' · ')}
                        onPick={it => { const u = it.unit || 'KG'; setRow('fabrics_json', i, { fabric_master_id: it.id, fabric_desc: it.fabric_name, composition: it.composition || '', gsm: it.gsm || '', dia_width: it.dia_width || '', unit: u, technique: String(u).toUpperCase() === 'MTR' ? 'Woven' : 'Knit' }); }} />
                    </td>
                    <td style={{ padding: 3 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.composition || ''} readOnly /></td>
                    <td style={{ padding: 3, width: 56 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.gsm || ''} readOnly /></td>
                    <td style={{ padding: 3, width: 70 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.dia_width || ''} readOnly /></td>
                    <td style={{ padding: 3, width: 84 }}><select style={inp} value={row.technique || 'Knit'} onChange={e => { const t = e.target.value; setRow('fabrics_json', i, { technique: t, unit: t === 'Woven' ? 'MTR' : 'KG' }); }}><option>Knit</option><option>Woven</option></select></td>
                    <td style={{ padding: 3, width: 56 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.unit || ''} readOnly /></td>
                    <td style={{ padding: 3, width: 100 }}><input style={inp} value={row.shade || ''} placeholder="Colour" onChange={e => setRow('fabrics_json', i, { shade: e.target.value })} /></td>
                    <td style={{ padding: 3, width: 80 }}><input style={inp} value={row.cpp || ''} placeholder="Qty" onChange={e => setRow('fabrics_json', i, { cpp: e.target.value })} /></td>
                    <td style={{ padding: 3, width: 28 }}><button type="button" onClick={() => delRow('fabrics_json', i)} style={{ border: 'none', background: 'none', color: '#E22D2D', cursor: 'pointer' }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={() => addRow('fabrics_json', emptyFabric)} style={addBtn}>+ Add Fabric Row</button>
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
              <div><label style={lbl}>Fabric Special Instructions</label><textarea style={{ ...inp, minHeight: 40 }} value={form.fabric_special_instructions || ''} onChange={e => f('fabric_special_instructions', e.target.value)} /></div>
              <div><label style={lbl}>Layer Plan</label><select style={inp} value={form.layer_plan_status || ''} onChange={e => f('layer_plan_status', e.target.value)}>{LAYER_PLAN_OPTS.map(o => <option key={o}>{o}</option>)}</select></div>
            </div>
          </Section>

          {/* Section 4 — Trims & Accessories (from Trims Master) */}
          <Section n={4} title="Trims & Accessories">
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Search & select a trim from the master — item, category, unit & CPP auto-fill. Enter only Colour/Spec and Supplier/Remarks.</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Trim (search master)', 'Category', 'Unit', 'CPP', 'Colour/Spec *', 'Supplier/Remarks *', ''].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#888', fontSize: 10, borderBottom: '1px solid #eee' }}>{h}</th>)}</tr></thead>
              <tbody>
                {(form.trims_json || []).map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: 3, minWidth: 160 }}>
                      <MasterSelect items={trimsMaster} value={row.item_name} placeholder="Search trim…"
                        labelOf={it => it.item_name}
                        subOf={it => [it.category, it.unit].filter(Boolean).join(' · ')}
                        onPick={it => setRow('trims_json', i, { trim_master_id: it.id, item_name: it.item_name, category: it.category || 'Label', unit: it.unit || 'PC', cpp: (it.standard_cpp ?? it.standard_rate ?? '') })} />
                    </td>
                    <td style={{ padding: 3, width: 90 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.category || ''} readOnly /></td>
                    <td style={{ padding: 3, width: 56 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.unit || ''} readOnly /></td>
                    <td style={{ padding: 3, width: 60 }}><input style={{ ...inp, background: '#F7F6F3', color: '#555' }} value={row.cpp || ''} readOnly /></td>
                    <td style={{ padding: 3 }}><input style={inp} value={row.spec || ''} placeholder="Colour/Spec" onChange={e => setRow('trims_json', i, { spec: e.target.value })} /></td>
                    <td style={{ padding: 3 }}><input style={inp} value={row.supplier || ''} placeholder="Supplier" onChange={e => setRow('trims_json', i, { supplier: e.target.value })} /></td>
                    <td style={{ padding: 3, width: 28 }}><button type="button" onClick={() => delRow('trims_json', i)} style={{ border: 'none', background: 'none', color: '#E22D2D', cursor: 'pointer' }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={() => addRow('trims_json', emptyTrim)} style={addBtn}>+ Add Trim Row</button>
          </Section>

          {/* Section 5 — Measurement Chart (upload) */}
          <Section n={5} title="Measurement Chart">
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Upload buyer measurement spec sheet (XLS/XLSX/PDF/JPG/PNG).</div>
            <Uploader accept={CHART_TYPES} label="📊 Upload Measurement Chart"
              onUploaded={m => setMedia('measurement_chart', m)} />
            {(form.media_json || {}).measurement_chart && (
              <FileChip thumb file={(form.media_json || {}).measurement_chart} onRemove={() => setMedia('measurement_chart', null)} />
            )}
          </Section>

          {/* Section 6 — Construction & Stitch */}
          <Section n={6} title="Construction & Stitch">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              {[['seam_type', 'Seam Type'], ['spi', 'SPI'], ['thread_type', 'Thread Type/Count'], ['thread_colour', 'Thread Colour'],
                ['collar_type', 'Collar Type'], ['cuff_type', 'Cuff Type'], ['placket_type', 'Placket Type'], ['hem_type', 'Hem Type']].map(([k, l]) => (
                  <div key={k}><label style={lbl}>{l}</label><input style={inp} value={form[k] || ''} onChange={e => f(k, e.target.value)} /></div>
                ))}
              <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Special Stitching</label><input style={inp} value={form.special_stitching || ''} onChange={e => f('special_stitching', e.target.value)} /></div>
              <div><label style={lbl}>Embroidery / Print</label>
                <select style={inp} value={form.embroidery_print || 'None'} onChange={e => f('embroidery_print', e.target.value)}><option>None</option><option>Embroidery</option><option>Print</option></select>
              </div>
              <div><label style={lbl}>{form.embroidery_print === 'Print' ? 'Print Type' : 'Placement'}</label>
                <input style={inp} value={form.embroidery_print === 'Print' ? (form.print_type || '') : (form.embroidery_placement || '')} onChange={e => f(form.embroidery_print === 'Print' ? 'print_type' : 'embroidery_placement', e.target.value)} />
              </div>
            </div>
          </Section>

          {/* Section 7 — Operation Breakdown. Editable while DRAFT; once issued/
              studied/approved it's READ-ONLY and shows the actual T&M study. */}
          <Section n={7} title={isDraft ? 'Operation Breakdown (for IE T&M Study)' : `Operation Breakdown${studyDone ? ' · T&M Study (read-only)' : ' (read-only)'}`}>
            {isDraft ? (<>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['#', 'Operation Name', 'Machine', 'Part/Component', 'Est SAM', 'Remarks', 'IE Image', ''].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#888', fontSize: 10, borderBottom: '1px solid #eee' }}>{h}</th>)}</tr></thead>
              <tbody>
                {(form.operations_json || []).map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: '4px 8px', color: '#aaa', width: 24 }}>{i + 1}</td>
                    <td style={{ padding: 3, minWidth: 170 }}><input style={inp} value={row.operation_name} onChange={e => setRow('operations_json', i, { operation_name: e.target.value })} /></td>
                    <td style={{ padding: 3, width: 80 }}><select style={inp} value={row.machine_type} onChange={e => setRow('operations_json', i, { machine_type: e.target.value })}>{MACHINE_TYPES.map(m => <option key={m}>{m}</option>)}</select></td>
                    <td style={{ padding: 3, width: 110 }}><input style={inp} value={row.part} onChange={e => setRow('operations_json', i, { part: e.target.value })} /></td>
                    <td style={{ padding: 3, width: 70 }}><input style={inp} type="number" step="0.001" value={row.est_sam} onChange={e => setRow('operations_json', i, { est_sam: e.target.value })} /></td>
                    <td style={{ padding: 3 }}><input style={inp} value={row.remarks} onChange={e => setRow('operations_json', i, { remarks: e.target.value })} /></td>
                    <td style={{ padding: 3, width: 92 }}>
                      {row.image_url
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <a href={row.image_url} target="_blank" rel="noreferrer" title={row.image_name}><img src={row.image_url} alt={row.image_name} style={{ height: 30, width: 30, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} /></a>
                            <button type="button" onClick={() => setRow('operations_json', i, { image_url: '', image_name: '' })} style={{ border: 'none', background: 'none', color: '#E22D2D', cursor: 'pointer', fontSize: 12 }}>✕</button>
                          </span>
                        : <Uploader accept={IMG_TYPES} label="📎 Image" style={{ padding: '4px 8px', fontSize: 10 }}
                            onUploaded={m => setRow('operations_json', i, { image_url: m.url, image_name: m.name })} />}
                    </td>
                    <td style={{ padding: 3, width: 28 }}><button type="button" onClick={() => delRow('operations_json', i)} style={{ border: 'none', background: 'none', color: '#E22D2D', cursor: 'pointer' }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
              <button type="button" onClick={() => addRow('operations_json', emptyOp, 60)} style={addBtn}>+ Add Operation {(form.operations_json || []).length >= 60 ? '(max 60)' : ''}</button>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#3B3A8F' }}>Total Estimated SAM: {totalEstSam.toFixed(3)} min</span>
            </div>
            </>) : (<>
            {/* Read-only view. Shows the actual studied SAM (`sam`) when present,
                else the original estimate; plus efficiency/bottleneck if studied. */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['#', 'Operation Name', 'Machine', 'Part/Component', studyDone ? 'SAM (min)' : 'Est SAM', ...(studyDone ? ['Eff %', 'Bottleneck'] : []), 'Remarks', 'IE Image'].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#888', fontSize: 10, borderBottom: '1px solid #eee' }}>{h}</th>)}</tr></thead>
              <tbody>
                {(form.operations_json || []).filter(o => o.operation_name).map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f3f3' }}>
                    <td style={{ padding: '6px 8px', color: '#aaa', width: 24 }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px' }}>{row.operation_name}{row.bottleneck && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#E22D2D' }}>⏱ bottleneck</span>}</td>
                    <td style={{ padding: '6px 8px' }}>{row.machine_type || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{row.part || '—'}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 700, color: '#3B3A8F' }}>{samOf(row) != null && samOf(row) !== '' ? Number(samOf(row)).toFixed(3) : '—'}</td>
                    {studyDone && <td style={{ padding: '6px 8px' }}>{row.efficiency != null && row.efficiency !== '' ? Number(row.efficiency).toFixed(0) + '%' : '—'}</td>}
                    {studyDone && <td style={{ padding: '6px 8px' }}>{row.bottleneck ? <span style={{ color: '#E22D2D', fontWeight: 700 }}>Yes</span> : '—'}</td>}
                    <td style={{ padding: '6px 8px', color: '#666' }}>{row.remarks || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{row.image_url ? <a href={row.image_url} target="_blank" rel="noreferrer" title={row.image_name}><img src={row.image_url} alt={row.image_name} style={{ height: 28, width: 28, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} /></a> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>{studyDone ? 'Total Actual SAM' : 'Total Estimated SAM'}: {totalSamShown.toFixed(3)} min</span>
            </div>
            </>)}
            <div style={{ fontSize: 10, color: '#888', marginTop: 6, fontStyle: 'italic' }}>Per-operation image = reference image for IE (stitch detail / operation sketch). {MACHINE_LEGEND}</div>

            {/* Section-level construction reference images (multiple) */}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px dashed #ddd' }}>
              <label style={lbl}>Construction Reference Images</label>
              <div style={{ fontSize: 11, color: '#888', margin: '2px 0 8px' }}>Upload stitch detail / construction reference images for the IE (JPG, PNG — multiple allowed).</div>
              <Uploader accept={IMG_TYPES} multiple label="🖼 Upload Construction Reference Images"
                onUploaded={metas => setMedia('construction_images', [...construction_images, ...(Array.isArray(metas) ? metas : [metas])])} />
              {construction_images.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                  {construction_images.map((img, k) => (
                    <div key={k} style={{ position: 'relative' }}>
                      <a href={img.url} target="_blank" rel="noreferrer"><img src={img.url} alt={img.name} title={img.name} style={{ height: 64, width: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} /></a>
                      <button type="button" onClick={() => setMedia('construction_images', construction_images.filter((_, j) => j !== k))}
                        style={{ position: 'absolute', top: -6, right: -6, background: '#E22D2D', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 11, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>

          {/* Section 8 — Special Instructions */}
          <Section n={8} title="Special Instructions">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={lbl}>Washing / Care Instructions</label><textarea style={{ ...inp, minHeight: 40 }} value={form.washing_instructions || ''} onChange={e => f('washing_instructions', e.target.value)} /></div>
              <div><label style={lbl}>Packing Method</label><select style={inp} value={form.packing_method || ''} onChange={e => f('packing_method', e.target.value)}>{PACKING_METHODS.map(p => <option key={p}>{p}</option>)}</select>
                <label style={{ ...lbl, marginTop: 8 }}>Hangtag / Barcode</label><input style={inp} value={form.hangtag_barcode || ''} onChange={e => f('hangtag_barcode', e.target.value)} /></div>
              <div><label style={lbl}>Buyer QC Reference</label><input style={inp} value={form.buyer_qc_reference || ''} onChange={e => f('buyer_qc_reference', e.target.value)} /></div>
              <div><label style={lbl}>Critical QC Points</label><input style={inp} value={form.critical_qc_points || ''} onChange={e => f('critical_qc_points', e.target.value)} /></div>
              <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Additional Notes</label><textarea style={{ ...inp, minHeight: 40 }} value={form.additional_notes || ''} onChange={e => f('additional_notes', e.target.value)} /></div>
            </div>
          </Section>

          {/* Section 9 — Attachments Checklist */}
          <Section n={9} title="Attachments Checklist">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px 24px' }}>
              {ATTACHMENT_ITEMS.map(([k, l]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ color: '#444' }}>{l}</span>
                  <select style={{ ...inp, width: 110 }} value={(form.attachments_json || {})[k] || 'No'} onChange={e => f('attachments_json', { ...form.attachments_json, [k]: e.target.value })}>
                    <option>Yes</option><option>No</option><option>To Follow</option>
                  </select>
                </div>
              ))}
            </div>
          </Section>

          {/* Section 10 — Sign-off */}
          <Section n={10} title="Sign-off & Revision History">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
              {[['merchandiser', 'Merchandiser', 'merchandiser_signed'], ['sampling_supervisor', 'Sampling Supervisor', 'sampling_supervisor_signed'],
                ['ie_officer', 'IE Officer', 'ie_officer_signed'], ['production_manager', 'Production Manager', 'production_manager_signed']].map(([role, label, flag]) => (
                  <button key={role} type="button" onClick={() => sign(role)} style={{ padding: '10px', borderRadius: 8, border: `1px solid ${form[flag] ? '#16a34a' : '#ddd'}`, background: form[flag] ? '#dcfce7' : '#F7F6F3', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: form[flag] ? '#166534' : '#555' }}>
                    {form[flag] ? '✓ Signed' : '✍ Sign'}<div style={{ fontSize: 10, fontWeight: 400, marginTop: 2 }}>{label}</div>
                  </button>
                ))}
            </div>
            {(form.revision_history || []).length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ background: '#F7F6F3' }}>{['Rev', 'Date', 'By', 'Reason'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>{h}</th>)}</tr></thead>
                <tbody>{(form.revision_history || []).map((r, i) => <tr key={i}><td style={{ padding: '4px 8px' }}>{r.rev}</td><td style={{ padding: '4px 8px' }}>{fmt(r.date)}</td><td style={{ padding: '4px 8px' }}>{r.by}</td><td style={{ padding: '4px 8px' }}>{r.reason}</td></tr>)}</tbody>
              </table>
            )}
          </Section>
        </div>

        {/* Footer actions — draft entry only while DRAFT; otherwise read-only
            (Print + Close), so an issued/approved bulletin can't be re-issued
            or overwritten as a draft from this modal. */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 0, background: 'white', borderRadius: '0 0 12px 12px' }}>
          <button type="button" onClick={doPrint} style={{ padding: '9px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>🖨 Print (A4)</button>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {!isDraft && <span style={{ fontSize: 12, color: '#888', marginRight: 4 }}>🔒 {SB_STATUS_LABEL[form.status] || form.status} — read-only</span>}
            <button type="button" onClick={onClose} style={{ padding: '9px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>{isDraft ? 'Cancel' : 'Close'}</button>
            {isDraft && <button type="button" disabled={saving} onClick={() => save()} style={{ padding: '9px 18px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : '💾 Save Draft'}</button>}
            {isDraft && <button type="button" disabled={saving} onClick={() => save('issue')} style={{ padding: '9px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>📤 Issue to Sampling Room</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
const addBtn = { marginTop: 8, padding: '5px 14px', background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };

// ════════════════════════════════════════════════════════════════════════════
// Sample Bulletin Tab (Merchandising)
// ════════════════════════════════════════════════════════════════════════════
const SB_STATUS_COLOR = {
  draft: '#5F5E5A', issued: '#854F0B', sample_making: '#9A6B00', sample_ready_tms: '#2A2970',
  tms_in_progress: '#2A2970', pending_pm_approval: '#9A3412', tms_done: '#166534', sample_ready: '#854F0B',
  sample_ready_merchant: '#854F0B', handed_to_merchant: '#166534', sample_accepted: '#166534',
};
export const sbStatusChip = (s) => {
  const color = SB_STATUS_COLOR[s] || '#5F5E5A';
  return <span style={{ background: color + '22', color, padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{SB_STATUS_LABEL[s] || s}</span>;
};

export function SampleBulletinTab({ user }) {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);   // bulletin being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null); // bulletin being accepted
  const [amending, setAmending] = useState(null);       // admin: bulletin being amended
  const [historyFor, setHistoryFor] = useState(null);   // admin: amendment history
  const [showClonePicker, setShowClonePicker] = useState(false); // "Copy from existing" picker
  const [cloneSearch, setCloneSearch] = useState('');
  const [linkingGpos, setLinkingGpos] = useState(null); // admin/mgmt: manage GPO→bulletin links

  const load = () => { setLoading(true); api.get('/sample-bulletins').then(r => setList(r.data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  // Copy from existing: deep-copy a source bulletin into a NEW editable draft. Nothing
  // is written until the merchant Saves (POST → new bulletin_no), so an abandoned clone
  // leaves NO orphan. No id ⇒ SampleBulletinForm save is a POST, never a PATCH of the
  // source. All content JSONB is JSON.parse/stringify deep-copied ⇒ zero shared refs, so
  // editing the clone (add/remove/edit operations) can never touch the source.
  const cloneBulletin = async (src) => {
    let detail = src;
    try { const r = await api.get('/sample-bulletins/' + src.id); if (r.data?.data) detail = r.data.data; } catch {}
    const deep = (v) => { try { return JSON.parse(JSON.stringify(typeof v === 'string' ? JSON.parse(v) : (v ?? null))); } catch { return null; } };
    const seed = { ...detail };
    // COPY (deep) the operations + style content
    seed.operations_json = deep(detail.operations_json) || [];
    seed.fabrics_json = deep(detail.fabrics_json);
    seed.trims_json = deep(detail.trims_json);
    seed.measurement_points = deep(detail.measurement_points);
    seed.size_ratio = deep(detail.size_ratio);
    // OVERRIDE identity → forces a NEW bulletin on save
    delete seed.id; delete seed.bulletin_no; delete seed.created_at; delete seed.updated_at;
    delete seed.merchandiser_name; delete seed.tms_by_name; delete seed.copied_from_date;
    seed.status = 'draft';
    // RESET the sign-off / T&M workflow — the clone earns its own study
    seed.merchandiser_signed = false; seed.sampling_supervisor_signed = false;
    seed.ie_officer_signed = false; seed.production_manager_signed = false;
    seed.tms_completed = false; seed.tms_completed_at = null; seed.tms_completed_by = null;
    seed.revision_no = 1; seed.revision_history = []; seed.bulletin_pdf_url = null;
    // BLANK style/order-specific fields (merchant re-enters for the new style)
    seed.style_no = ''; seed.gpo_no = ''; seed.media_json = {}; seed.attachments_json = {};
    seed.issue_date = ''; seed.delivery_date = ''; seed.order_qty = ''; seed.sample_qty = '';
    // PROVENANCE
    seed.copied_from_bulletin_id = src.id; seed.copied_from_bulletin_no = src.bulletin_no;
    seed._copied_from_date = detail.created_at;
    setShowClonePicker(false); setCloneSearch('');
    setEditing(seed); setShowForm(true);
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try { await api.post(`/sample-bulletins/${rejecting.id}/reject`, { reason: rejectReason.trim() }); setRejecting(null); setRejectReason(''); load(); }
    catch (e) { alert(e.response?.data?.error || 'Reject failed'); }
    finally { setBusy(false); }
  };

  // Merchant accepts the sample → moves to Cost Estimation. On success, offer to
  // jump straight into the OE estimate form (where the bulletin can be linked).
  const acceptSample = async (b) => {
    setAcceptingId(b.id);
    try {
      await api.post(`/sample-bulletins/${b.id}/accept-sample`);
      load();
      if (window.confirm(`Sample ${b.bulletin_no} accepted. Create the Cost Estimation linked to this bulletin now?`)) {
        navigate('/order-execution');
      }
    } catch (e) { alert(e.response?.data?.error || 'Accept failed'); }
    finally { setAcceptingId(null); }
  };

  const statusChip = sbStatusChip;
  const pj = (v, d) => { try { return typeof v === 'string' ? JSON.parse(v) : (v ?? d); } catch { return d; } };
  const awaiting = list.filter(b => b.status === 'sample_ready_merchant');

  return (
    <div style={{ padding: 16 }}>
      {/* ── Sample Review — samples awaiting the Merchandiser's accept/reject ── */}
      {awaiting.length > 0 && (
        <div style={{ marginBottom: 20, border: '2px solid #F59E0B', borderRadius: 12, overflow: 'hidden', background: '#FFFBEB' }}>
          <div style={{ background: '#FEF3C7', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #FDE68A' }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#92400E' }}>Samples Awaiting Your Review</div>
              <div style={{ fontSize: 12, color: '#B45309' }}>{awaiting.length} sample(s) ready from the Sampling Room — accept to proceed to Cost Estimation, or reject with a reason.</div>
            </div>
            <span style={{ marginLeft: 'auto', background: '#F59E0B', color: '#fff', fontWeight: 800, borderRadius: 20, padding: '2px 12px', fontSize: 14 }}>{awaiting.length}</span>
          </div>
          <div style={{ padding: 16, display: 'grid', gap: 16 }}>
            {awaiting.map(b => {
              const media = pj(b.media_json, {}) || {};
              const photos = media.sample_photos || {};
              const handover = media.handover;
              const ops = (pj(b.operations_json, []) || []).filter(o => o.operation_name);
              const totalSam = b.total_sam_actual != null ? Number(b.total_sam_actual) : ops.reduce((s, o) => s + (parseFloat(o.sam) || 0), 0);
              const bottleneck = ops.reduce((x, o) => (parseFloat(o.sam) || 0) > (parseFloat(x?.sam) || 0) ? o : x, null);
              const effs = ops.map(o => parseFloat(o.efficiency)).filter(e => e > 0);
              const avgEff = effs.length ? effs.reduce((a, c) => a + c, 0) / effs.length : 0;
              return (
                <div key={b.id} style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 10, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                    <span style={{ fontWeight: 800, color: '#3B3A8F', fontSize: 15 }}>{b.bulletin_no}</span>
                    <span style={{ fontSize: 12, color: '#666' }}>{b.style_no || '—'} · {b.buyer || '—'} · {b.sample_type || '—'}</span>
                    {b.revision_no > 1 && <span style={{ background: '#FAEEDA', color: '#9A6B00', padding: '1px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>{sampleOrdinal(b.revision_no)}</span>}
                  </div>

                  {/* Sample photos (front / back / detail) */}
                  <div style={{ ...lbl, marginBottom: 6 }}>Sample Photos</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    {[['front', 'Front'], ['back', 'Back'], ['detail', 'Detail']].map(([k, l]) => (
                      <div key={k} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>{l}</div>
                        {photos[k]?.url
                          ? <a href={photos[k].url} target="_blank" rel="noreferrer"><img src={photos[k].url} alt={l} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} /></a>
                          : <div style={{ width: 100, height: 100, borderRadius: 8, border: '1px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11, background: '#FAFAFA' }}>No photo</div>}
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 16, marginBottom: 14 }}>
                    {/* Handover checklist */}
                    <div>
                      <div style={lbl}>Handover Checklist</div>
                      {handover ? (
                        <div style={{ fontSize: 12, color: '#444' }}>
                          <div style={{ marginBottom: 4 }}>Pieces: <strong>{handover.pieces_count || '—'}</strong> · Size: <strong>{handover.size || '—'}</strong></div>
                          {[['fabric_swatches', 'Fabric swatches'], ['trim_card', 'Trim card'], ['measurement_sheet', 'Measurement sheet'], ['special_instructions', 'Special instructions']].map(([k, l]) => (
                            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                              <span style={{ color: handover[k] ? '#166534' : '#bbb', fontWeight: 700 }}>{handover[k] ? '✓' : '○'}</span>{l}
                            </div>
                          ))}
                          {handover.notes && <div style={{ marginTop: 6, fontStyle: 'italic', color: '#666' }}>“{handover.notes}”</div>}
                          {handover.handed_by && <div style={{ marginTop: 6, fontSize: 10, color: '#999' }}>By {handover.handed_by} · {fmt(handover.handed_at)}</div>}
                        </div>
                      ) : <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>Handover details not yet recorded by the Sampling Room.</div>}
                    </div>

                    {/* T&M summary */}
                    <div>
                      <div style={lbl}>T&M Summary</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                        <div style={miniCard}><div style={miniLbl}>Total SAM</div><div style={miniVal}>{totalSam ? totalSam.toFixed(3) : '—'}<span style={{ fontSize: 10, fontWeight: 500 }}> min</span></div></div>
                        <div style={miniCard}><div style={miniLbl}>Bottleneck</div><div style={{ ...miniVal, fontSize: 12, color: '#E22D2D' }}>{bottleneck?.operation_name || '—'}</div></div>
                        <div style={miniCard}><div style={miniLbl}>Efficiency</div><div style={miniVal}>{avgEff ? avgEff.toFixed(0) + '%' : '—'}</div></div>
                      </div>
                    </div>
                  </div>

                  {/* Accept / Reject */}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button type="button" disabled={acceptingId === b.id} onClick={() => acceptSample(b)}
                      style={{ flex: 1, padding: '11px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: acceptingId === b.id ? 'wait' : 'pointer' }}>
                      {acceptingId === b.id ? 'Accepting…' : '✅ Accept Sample — Proceed to Cost Estimation'}
                    </button>
                    <button type="button" onClick={() => { setRejecting(b); setRejectReason(''); }}
                      style={{ flex: 1, padding: '11px', background: '#E22D2D', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                      ❌ Reject Sample — Send for Revision
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: '#888' }}>{list.length} sample bulletin(s)</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => { setCloneSearch(''); setShowClonePicker(true); }} title="Clone a previous bulletin as a starting point for a similar style" style={{ padding: '7px 16px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>📋 Copy from Existing</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>+ New Sample Bulletin</button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>{['Bulletin No', 'Style No', 'Buyer', 'Sample Type', 'Issue Date', 'Issued To', 'TMS Status', 'SAM (Est/Act)', 'Status', 'Actions'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>
              : list.length === 0 ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No sample bulletins yet. Click + New Sample Bulletin.</td></tr>
                : list.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: '#3B3A8F', whiteSpace: 'nowrap' }}>
                      {b.bulletin_no}
                      {b.revision_no > 1 && <span title="Revision count" style={{ marginLeft: 6, background: '#FAEEDA', color: '#9A6B00', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{sampleOrdinal(b.revision_no)}</span>}
                      {b.copied_from_bulletin_no && <span title={`Copied from ${b.copied_from_bulletin_no}${b.copied_from_date ? ' · ' + fmt(b.copied_from_date) : ''}`} style={{ display: 'block', fontWeight: 600, fontSize: 9, color: '#9A6B00', marginTop: 1 }}>📋 from {b.copied_from_bulletin_no}</span>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{b.style_no || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{b.buyer || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{b.sample_type || '—'}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmt(b.issue_date)}</td>
                    <td style={{ padding: '8px 10px' }}>{b.issued_to || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{b.tms_completed ? <span style={{ color: '#166534', fontWeight: 600 }}>✓ Done</span> : <StatusPipeline status={b.status} />}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{b.total_sam_estimated ? Number(b.total_sam_estimated).toFixed(2) : '—'} / {b.total_sam_actual ? <strong style={{ color: '#166534' }}>{Number(b.total_sam_actual).toFixed(2)}</strong> : '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{statusChip(b.status)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => { setEditing(b); setShowForm(true); }} style={actBtn}>Open</button>
                      {['admin', 'management'].includes(user?.role) &&
                        <button title="Manage GPO links for this bulletin" onClick={() => setLinkingGpos(b)} style={{ ...actBtn, background: '#EEF2FF', color: '#3B3A8F' }}>🔗 GPOs</button>}
                      {(b.status === 'sample_ready_merchant' || b.status === 'handed_to_merchant') &&
                        <button onClick={() => { setRejecting(b); setRejectReason(''); }} style={{ ...actBtn, background: '#FCEBEB', color: '#A32D2D' }}>✗ Reject</button>}
                      {sbIsLocked(b.status) && user?.role === 'admin' &&
                        <button title="Admin: amend this approved bulletin" onClick={() => setAmending(b)} style={{ ...actBtn, background: '#FEF3C7', color: '#92400E' }}>✏️ Amend</button>}
                      {sbIsLocked(b.status) && user?.role === 'admin' &&
                        <button title="Amendment history" onClick={() => setHistoryFor(b)} style={{ ...actBtn, background: '#F0F0F0', color: '#555' }}>🕑 History</button>}
                      {sbIsLocked(b.status) && user?.role !== 'admin' &&
                        <span title="Approved — locked" style={{ fontSize: 11, color: '#999', marginLeft: 4 }}>🔒 locked</span>}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <ListFooter shown={list.length} total={list.length} noun="bulletins" />
      {showClonePicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, overflowY: 'auto', padding: '40px 0' }}>
          <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 780, boxShadow: '0 8px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ background: '#2D2C72', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><div style={{ fontSize: 16, fontWeight: 800, color: 'white' }}>📋 Copy from Existing Bulletin</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', marginTop: 2 }}>Pick a source — its operations, fabrics, trims &amp; measurements are copied into a new bulletin (the source is never changed).</div></div>
              <button type="button" onClick={() => setShowClonePicker(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <input autoFocus placeholder="Search by bulletin no, style no or buyer…" value={cloneSearch} onChange={e => setCloneSearch(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />
              <div style={{ maxHeight: '52vh', overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ background: '#F7F6F3' }}>{['Bulletin No', 'Style', 'Buyer', 'Type', 'SAM', 'Status', ''].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', position: 'sticky', top: 0, background: '#F7F6F3' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {list.filter(b => { const q = cloneSearch.trim().toLowerCase(); if (!q) return true; return [b.bulletin_no, b.style_no, b.buyer, b.garment_description].some(v => String(v || '').toLowerCase().includes(q)); }).map(b => (
                      <tr key={b.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '7px 10px', fontWeight: 700, color: '#3B3A8F' }}>{b.bulletin_no}</td>
                        <td style={{ padding: '7px 10px', fontWeight: 600 }}>{b.style_no || '—'}</td>
                        <td style={{ padding: '7px 10px' }}>{b.buyer || '—'}</td>
                        <td style={{ padding: '7px 10px' }}>{b.sample_type || '—'}</td>
                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{b.total_sam_actual ? Number(b.total_sam_actual).toFixed(2) : (b.total_sam_estimated ? Number(b.total_sam_estimated).toFixed(2) : '—')}</td>
                        <td style={{ padding: '7px 10px' }}>{statusChip(b.status)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}><button onClick={() => cloneBulletin(b)} style={{ padding: '4px 12px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Copy →</button></td>
                      </tr>
                    ))}
                    {list.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No bulletins to copy from yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {showForm && <SampleBulletinForm bulletin={editing} user={user} onClose={() => setShowForm(false)} onSaved={load} />}
      {amending && <AmendModal bulletin={amending} user={user} onClose={() => setAmending(null)} onSaved={() => { setAmending(null); load(); }} />}
      {historyFor && <AmendHistoryModal bulletin={historyFor} onClose={() => setHistoryFor(null)} />}
      {linkingGpos && <LinkedGposModal bulletin={linkingGpos} user={user} onClose={() => setLinkingGpos(null)} />}

      {/* Merchant rejection modal */}
      {rejecting && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 460, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#A32D2D' }}>Reject Sample — {rejecting.bulletin_no}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>The sample goes back to the Sampling Room for revision ({sampleOrdinal((rejecting.revision_no || 1) + 1)}). State the reason clearly.</p>
            <textarea autoFocus value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g. Collar shape incorrect, measurement off at chest, wrong shade…"
              style={{ ...inp, minHeight: 90, marginBottom: 14 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" onClick={() => setRejecting(null)} style={{ padding: '9px 16px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={busy || !rejectReason.trim()} onClick={submitReject}
                style={{ padding: '9px 16px', background: rejectReason.trim() ? '#E22D2D' : '#e9a9a9', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>
                {busy ? 'Rejecting…' : '✗ Reject & Send Back'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const actBtn = { padding: '3px 9px', background: '#EEEDF8', color: '#3B3A8F', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600, marginRight: 5 };
const miniCard = { background: '#F7F6F3', borderRadius: 8, padding: '8px 10px' };
const miniLbl = { fontSize: 9, color: '#888', textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 };
const miniVal = { fontSize: 16, fontWeight: 800, color: '#3B3A8F' };

// ════════════════════════════════════════════════════════════════════════════
// IE T&M Study
// ════════════════════════════════════════════════════════════════════════════
function TMStudyForm({ bulletin, onClose, onSaved, user }) {
  const [ops, setOps] = useState(() => {
    const base = (() => { try { return typeof bulletin.operations_json === 'string' ? JSON.parse(bulletin.operations_json) : (bulletin.operations_json || []); } catch { return []; } })();
    return (base.length ? base : [{ operation_name: '', machine_type: 'SNL' }]).map(o => ({
      operation_name: o.operation_name || '', machine_type: o.machine_type || 'SNL',
      observed_time: o.observed_time || '', rating: o.rating ?? 100, allowance: o.allowance ?? 10, actual_output: o.actual_output || '',
    }));
  });
  const [saving, setSaving] = useState(false);

  const compute = (o) => {
    const observed = parseFloat(o.observed_time) || 0;
    const rating = parseFloat(o.rating) || 0;
    const allowance = parseFloat(o.allowance) || 0;
    const basic = observed * rating / 100;                 // seconds
    const sam = basic * (1 + allowance / 100) / 60;        // minutes
    const prodHr = sam > 0 ? 60 / sam : 0;
    const actual = parseFloat(o.actual_output) || 0;
    const eff = prodHr > 0 ? (actual / prodHr) * 100 : 0;
    return { basic, sam, prodHr, eff };
  };
  const computed = ops.map(compute);
  const maxSam = Math.max(0, ...computed.map(c => c.sam));
  const totalSam = computed.reduce((s, c) => s + c.sam, 0);
  const bottleneckIdx = computed.findIndex(c => c.sam === maxSam && maxSam > 0);
  const avgEff = computed.filter(c => c.eff > 0).length ? computed.filter(c => c.eff > 0).reduce((s, c) => s + c.eff, 0) / computed.filter(c => c.eff > 0).length : 0;

  const setOp = (i, patch) => setOps(ops.map((o, j) => j === i ? { ...o, ...patch } : o));

  const complete = async () => {
    if (!confirmSam(ops, totalSam)) return;   // warn (not block) on an out-of-band SAM before saving the study
    setSaving(true);
    try {
      const payloadOps = ops.map((o, i) => ({ ...o, basic_time: computed[i].basic, sam: computed[i].sam, prod_per_hr: computed[i].prodHr, efficiency: computed[i].eff, bottleneck: i === bottleneckIdx }));
      await api.post('/sample-bulletins/' + bulletin.id + '/tms', {
        operations: payloadOps, total_sam_actual: totalSam,
        bottleneck_operation: bottleneckIdx >= 0 ? ops[bottleneckIdx].operation_name : null,
      });
      onSaved(); onClose();
    } catch (e) { alert(e.response?.data?.error || 'Failed to save T&M study'); }
    finally { setSaving(false); }
  };

  const th = { padding: '6px 6px', textAlign: 'center', color: '#888', fontSize: 10, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };
  const td = { padding: 2, textAlign: 'center', fontSize: 11 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '20px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '96%', maxWidth: 1280, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: 16, color: 'rgba(255,255,255,0.72)' }}>⏱ T&M Study — {bulletin.bulletin_no}</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.72)' }}>Style {bulletin.style_no || '—'} · {bulletin.sample_type || '—'} · IE: {user?.name} · {fmt(new Date())}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'rgba(255,255,255,0.85)', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 20, maxHeight: '74vh', overflowY: 'auto' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['#', 'Operation', 'M/C', 'Obs Time (s)', 'Rating %', 'Allow %', 'Basic (s)', 'SAM (min)', 'Prod/Hr', 'Actual Out', 'Eff %', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {ops.map((o, i) => {
                  const c = computed[i];
                  const isBottle = i === bottleneckIdx;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', background: isBottle ? '#fff7ed' : 'white' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, minWidth: 170, textAlign: 'left' }}><input style={inp} value={o.operation_name} onChange={e => setOp(i, { operation_name: e.target.value })} /></td>
                      <td style={{ ...td, width: 70 }}><select style={inp} value={o.machine_type} onChange={e => setOp(i, { machine_type: e.target.value })}>{MACHINE_TYPES.map(m => <option key={m}>{m}</option>)}</select></td>
                      <td style={{ ...td, width: 80 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.observed_time} onChange={e => setOp(i, { observed_time: e.target.value })} /></td>
                      <td style={{ ...td, width: 64 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.rating} onChange={e => setOp(i, { rating: e.target.value })} /></td>
                      <td style={{ ...td, width: 64 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.allowance} onChange={e => setOp(i, { allowance: e.target.value })} /></td>
                      <td style={{ ...td, color: '#666' }}>{c.basic.toFixed(2)}</td>
                      <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{c.sam.toFixed(3)}</td>
                      <td style={{ ...td, color: '#666' }}>{c.prodHr.toFixed(0)}</td>
                      <td style={{ ...td, width: 80 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.actual_output} onChange={e => setOp(i, { actual_output: e.target.value })} /></td>
                      <td style={{ ...td, fontWeight: 600, color: c.eff >= 100 ? '#166534' : c.eff >= 80 ? '#854F0B' : '#A32D2D' }}>{c.eff.toFixed(0)}</td>
                      <td style={td}>{isBottle ? <span title="Bottleneck" style={{ fontSize: 10 }}>🔴 BOTTLE</span> : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setOps([...ops, { operation_name: '', machine_type: 'SNL', observed_time: '', rating: 100, allowance: 10, actual_output: '' }])} style={addBtn}>+ Add Operation</button>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginTop: 18 }}>
            {[['Total Operations', ops.filter(o => o.operation_name).length], ['Total SAM (min)', totalSam.toFixed(3)], ['Bottleneck SAM', maxSam.toFixed(3)],
              ['Bottleneck Op', bottleneckIdx >= 0 ? ops[bottleneckIdx].operation_name || '—' : '—'], ['Avg Efficiency %', avgEff.toFixed(0)]].map(([l, v], i) => (
                <div key={i} style={{ background: '#F7F6F3', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#3B3A8F' }}>{v}</div>
                </div>
              ))}
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} style={{ padding: '9px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button type="button" disabled={saving} onClick={complete} style={{ padding: '9px 18px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>{saving ? 'Saving…' : '✓ Complete T&M Study'}</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Linked GPOs (mig 108 reference model) — admin + management manage the many:1
// GPO→bulletin links. Link picks an estimate-less candidate GPO; unlink is admin-only
// (it can strand a line setup). Guard errors from the API are surfaced verbatim — they
// teach (e.g. "GPO-A is already costed against SB-X via its estimate…").
// ════════════════════════════════════════════════════════════════════════════
function LinkedGposModal({ bulletin, user, onClose }) {
  const isAdmin = user?.role === 'admin';
  const [linked, setLinked] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');       // guard-error banner (teaches — shown, not swallowed)
  const [notice, setNotice] = useState(''); // success / stale-warning banner
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unlinking, setUnlinking] = useState(null); // gpo_no pending unlink
  const [unlinkReason, setUnlinkReason] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/sample-bulletins/${bulletin.id}/linked-gpos`).then(r => r.data.data || []).catch(() => []),
      api.get(`/sample-bulletins/${bulletin.id}/link-log`).then(r => r.data.data || []).catch(() => []),
    ]).then(([lg, ll]) => { setLinked(lg); setLog(ll); }).finally(() => setLoading(false));
  };
  useEffect(load, [bulletin.id]);

  const openPicker = () => {
    setErr(''); setNotice(''); setStale(false); setPicker(true); setSearch(''); setCandLoading(true);
    api.get('/sample-bulletins/linkable-gpos')
      .then(r => setCandidates(r.data.data || []))
      .catch(e => setErr(e.response?.data?.error || 'Failed to load candidate GPOs'))
      .finally(() => setCandLoading(false));
  };

  const linkGpo = async (gpo_no) => {
    setErr(''); setNotice(''); setStale(false); setBusy(true);
    try {
      const r = await api.post('/sample-bulletins/link-gpo', { gpo_no, sample_bulletin_id: bulletin.id });
      setNotice(r.data?.already_linked ? `${gpo_no} was already linked here.` : `Linked ${gpo_no} → ${bulletin.bulletin_no}.`);
      setPicker(false); load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Link failed.');   // 400/409 guard message, verbatim
    } finally { setBusy(false); }
  };

  const submitUnlink = async () => {
    if (!unlinkReason.trim()) return;
    setErr(''); setNotice(''); setStale(false); setBusy(true);
    try {
      const r = await api.patch('/sample-bulletins/unlink-gpo', { gpo_no: unlinking, reason: unlinkReason.trim() });
      if (r.data?.stale_warning) { setStale(true); setNotice(r.data.message || `Unlinked ${unlinking}. A line setup / capacity derived from that link is now STALE and must be re-derived.`); }
      else setNotice(`Unlinked ${unlinking}.`);
      setUnlinking(null); setUnlinkReason(''); load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Unlink failed.');
    } finally { setBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const cands = candidates.filter(c => !q || [c.gpo_no, c.style_no, c.buyer, c.order_no].some(v => String(v || '').toLowerCase().includes(q)));
  const frozenBadge = { fontSize: 10, fontWeight: 700, color: '#9A3412', background: '#FFEDD5', padding: '1px 6px', borderRadius: 6, marginLeft: 6 };
  const th = { padding: '7px 10px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', position: 'sticky', top: 0, background: '#F7F6F3' };
  const cell = { padding: '7px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, overflowY: 'auto', padding: '40px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '94%', maxWidth: 900, boxShadow: '0 8px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ background: '#2D2C72', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'white' }}>🔗 Linked GPOs — {bulletin.bulletin_no}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', marginTop: 2 }}>Style {bulletin.style_no || '—'} · one bulletin can serve many GPOs (reference model). Re-timing this bulletin updates every linked GPO.</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: 18 }}>
          {err && (
            <div style={{ background: '#FCEBEB', border: '1px solid #F3B4B4', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span><strong>⚠ </strong>{err}</span>
              <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer', fontWeight: 700 }}>✕</button>
            </div>
          )}
          {notice && (
            <div style={{ background: stale ? '#FEF3C7' : '#DCFCE7', border: `1px solid ${stale ? '#FCD34D' : '#86EFAC'}`, color: stale ? '#92400E' : '#166534', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span>{stale ? '⚠ ' : '✓ '}{notice}</span>
              <button onClick={() => { setNotice(''); setStale(false); }} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 700 }}>✕</button>
            </div>
          )}

          {/* Currently linked */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2D2C72' }}>Currently linked <span style={{ color: '#888', fontWeight: 500 }}>({linked.length})</span></div>
            <button onClick={openPicker} disabled={busy} style={{ padding: '7px 14px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12.5, cursor: busy ? 'default' : 'pointer', fontWeight: 700 }}>+ Link GPO</button>
          </div>
          <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['GPO No', 'Style', 'Buyer', 'Qty', 'Linked by', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 12 }}>Loading…</td></tr>}
                {!loading && linked.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 12 }}>No GPOs linked to this bulletin yet.</td></tr>}
                {!loading && linked.map(g => (
                  <tr key={g.gpo_no}>
                    <td style={{ ...cell, fontWeight: 700, color: '#3B3A8F' }}>{g.gpo_no}{g.frozen && <span style={frozenBadge} title="A line setup / capacity was already derived from this link">frozen</span>}</td>
                    <td style={cell}>{g.style_no || '—'}</td>
                    <td style={cell}>{g.buyer || '—'}</td>
                    <td style={cell}>{g.order_qty != null ? g.order_qty : '—'}</td>
                    <td style={cell}>{g.linked_by_name || '—'}<div style={{ color: '#999', fontSize: 10 }}>{fmt(g.linked_at)}</div></td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      {isAdmin
                        ? <button onClick={() => { setUnlinking(g.gpo_no); setUnlinkReason(''); setErr(''); setNotice(''); }} style={{ padding: '4px 10px', background: '#FCEBEB', color: '#A32D2D', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Unlink</button>
                        : <span title="Admin only" style={{ fontSize: 10, color: '#bbb' }}>🔒</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Link history */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2D2C72', marginBottom: 8 }}>Link history</div>
          <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', maxHeight: '26vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['When', 'Action', 'GPO', 'Reason', 'By'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {log.length === 0 && <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#aaa', fontSize: 12 }}>No link/unlink history yet.</td></tr>}
                {log.map(l => (
                  <tr key={l.id}>
                    <td style={{ ...cell, whiteSpace: 'nowrap', color: '#888' }}>{fmt(l.acted_at)}</td>
                    <td style={cell}><span style={{ fontSize: 11, fontWeight: 700, color: l.action === 'unlink' ? '#A32D2D' : '#166534' }}>{l.action}{l.action === 'unlink' && l.stale_warning ? ' ⚠' : ''}</span></td>
                    <td style={{ ...cell, fontWeight: 600 }}>{l.gpo_no}</td>
                    <td style={{ ...cell, color: '#555' }}>{l.reason || '—'}</td>
                    <td style={cell}>{l.acted_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* + Link GPO picker */}
      {picker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1200, overflowY: 'auto', padding: '50px 0' }}>
          <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 820, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ background: '#3B3A8F', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><div style={{ fontSize: 15, fontWeight: 800, color: 'white' }}>+ Link a GPO to {bulletin.bulletin_no}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', marginTop: 2 }}>Estimate-less, unlinked candidate GPOs. A <span style={{ ...frozenBadge, marginLeft: 0 }}>frozen</span> GPO already has a line setup/capacity.</div></div>
              <button type="button" onClick={() => setPicker(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 16 }}>
              <input autoFocus placeholder="Search by GPO no, style, buyer or order…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />
              <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['GPO No', 'Style', 'Buyer', 'Order', 'Qty', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {candLoading && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 12 }}>Loading candidates…</td></tr>}
                    {!candLoading && cands.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 12 }}>{candidates.length ? 'No candidate matches your search.' : 'No linkable GPOs — every active estimate-less GPO is already linked.'}</td></tr>}
                    {!candLoading && cands.map(c => (
                      <tr key={c.gpo_no}>
                        <td style={{ ...cell, fontWeight: 700, color: '#3B3A8F' }}>{c.gpo_no}{c.frozen && <span style={frozenBadge} title="Already has a line setup / capacity">frozen</span>}</td>
                        <td style={cell}>{c.style_no || '—'}</td>
                        <td style={cell}>{c.buyer || '—'}</td>
                        <td style={cell}>{c.order_no || '—'}</td>
                        <td style={cell}>{c.order_qty != null ? c.order_qty : '—'}</td>
                        <td style={{ ...cell, textAlign: 'right' }}><button onClick={() => linkGpo(c.gpo_no)} disabled={busy} style={{ padding: '4px 12px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, cursor: busy ? 'default' : 'pointer', fontWeight: 600 }}>Link →</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlink reason (admin) */}
      {unlinking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div style={{ background: 'white', borderRadius: 12, width: '90%', maxWidth: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#A32D2D', marginBottom: 6 }}>Unlink {unlinking}?</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>If a line setup or capacity was derived from this link, it becomes <strong>stale</strong> and must be re-derived. A reason is required and recorded in the audit log.</div>
            <textarea autoFocus value={unlinkReason} onChange={e => setUnlinkReason(e.target.value)} placeholder="Why is this link being removed?" style={{ width: '100%', minHeight: 70, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => { setUnlinking(null); setUnlinkReason(''); }} style={{ padding: '8px 16px', background: '#F0F0F0', color: '#555', border: 'none', borderRadius: 6, fontSize: 12.5, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={submitUnlink} disabled={!unlinkReason.trim() || busy} style={{ padding: '8px 16px', background: (!unlinkReason.trim() || busy) ? '#E5A5A5' : '#A32D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 12.5, cursor: (!unlinkReason.trim() || busy) ? 'default' : 'pointer', fontWeight: 700 }}>{busy ? 'Unlinking…' : 'Unlink'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Admin Amendment of an APPROVED (locked) bulletin — reason required, audited,
// SAM recomputed within the bulletin, warns (never cascades) on linked costings.
// ════════════════════════════════════════════════════════════════════════════
const AMEND_HEADER_FIELDS = [
  ['buyer', 'Buyer'], ['style_no', 'Style No'], ['garment_description', 'Garment Description'],
  ['sample_type', 'Sample Type'], ['order_qty', 'Order Qty'], ['sample_qty', 'Sample Qty'], ['delivery_date', 'Delivery Date'],
];

function AmendModal({ bulletin, user, onClose, onSaved }) {
  const [header, setHeader] = useState(() => {
    const h = {}; AMEND_HEADER_FIELDS.forEach(([k]) => { h[k] = bulletin[k] ?? ''; }); return h;
  });
  const [ops, setOps] = useState(() => {
    const base = (() => { try { return typeof bulletin.operations_json === 'string' ? JSON.parse(bulletin.operations_json) : (bulletin.operations_json || []); } catch { return []; } })();
    return base.map(o => ({
      operation_name: o.operation_name || '', machine_type: o.machine_type || 'SNL',
      observed_time: o.observed_time ?? '', rating: o.rating ?? 100, allowance: o.allowance ?? 10, actual_output: o.actual_output ?? '',
    }));
  });
  const [opsDirty, setOpsDirty] = useState(false);
  const [reason, setReason] = useState('');
  const [linked, setLinked] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(true);
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/sample-bulletins/${bulletin.id}/linked-costings`)
      .then(r => setLinked(r.data.data?.linked || []))
      .catch(() => {})
      .finally(() => setLoadingLinked(false));
  }, [bulletin.id]);

  // Same math as the T&M study + the ×60 correction (server recomputes authoritatively too).
  const compute = (o) => {
    const observed = parseFloat(o.observed_time) || 0;
    const rating = parseFloat(o.rating) || 0;
    const allowance = parseFloat(o.allowance) || 0;
    const basic = observed * rating / 100;
    const sam = basic * (1 + allowance / 100) / 60;
    const prodHr = sam > 0 ? 60 / sam : 0;
    const actual = parseFloat(o.actual_output) || 0;
    const eff = prodHr > 0 ? (actual / prodHr) * 100 : 0;
    return { basic, sam, prodHr, eff };
  };
  const computed = ops.map(compute);
  const totalSam = computed.reduce((s, c) => s + c.sam, 0);
  const maxSam = Math.max(0, ...computed.map(c => c.sam));
  const bottleneckIdx = computed.findIndex(c => c.sam === maxSam && maxSam > 0);
  const origTotal = bulletin.total_sam_actual != null ? Number(bulletin.total_sam_actual) : null;

  const setOp = (i, patch) => { setOpsDirty(true); setOps(ops.map((o, j) => j === i ? { ...o, ...patch } : o)); };
  const setH = (k, v) => setHeader(p => ({ ...p, [k]: v }));

  const changedHeader = () => {
    const c = {};
    AMEND_HEADER_FIELDS.forEach(([k]) => { if (String(header[k] ?? '') !== String(bulletin[k] ?? '')) c[k] = header[k] === '' ? null : header[k]; });
    return c;
  };
  const headerChanges = changedHeader();
  const hasChanges = Object.keys(headerChanges).length > 0 || opsDirty;
  const needsAck = linked.length > 0;
  const canSave = !!reason.trim() && hasChanges && (!needsAck || ack) && !saving;

  const save = async () => {
    if (opsDirty && !confirmSam(ops, totalSam)) return;   // same guard on re-timed ops during an admin amend
    setSaving(true);
    try {
      const payload = { reason: reason.trim(), costing_acknowledged: needsAck ? ack : true };
      if (Object.keys(headerChanges).length) payload.changes = headerChanges;
      if (opsDirty) payload.operations = ops.map((o, i) => ({ ...o, basic_time: computed[i].basic, sam: computed[i].sam, prod_per_hr: computed[i].prodHr, efficiency: computed[i].eff, bottleneck: i === bottleneckIdx }));
      const r = await api.post(`/sample-bulletins/${bulletin.id}/amend`, payload);
      alert(`Amended ${bulletin.bulletin_no} — changed: ${(r.data.amended_fields || []).join(', ') || 'saved'}.`);
      onSaved();
    } catch (e) {
      if (e.response?.status === 409) { setLinked(e.response.data?.data?.linked || linked); setAck(false); alert(e.response.data?.error || 'Confirm the linked-costing warning first.'); }
      else alert(e.response?.data?.error || 'Amendment failed');
    } finally { setSaving(false); }
  };

  const th = { padding: '6px 6px', textAlign: 'center', color: '#888', fontSize: 10, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };
  const td = { padding: 2, textAlign: 'center', fontSize: 11 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1200, overflowY: 'auto', padding: '20px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '96%', maxWidth: 1280, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#92400E', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: 16 }}>✏️ Amend Approved Bulletin — {bulletin.bulletin_no}</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>Admin correction · {user?.name} · every change is logged with your reason</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'rgba(255,255,255,0.85)', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 20, maxHeight: '74vh', overflowY: 'auto' }}>
          {/* Linked-costing warning */}
          {!loadingLinked && linked.length > 0 && (
            <div style={{ border: '1.5px solid #F59E0B', background: '#FFFBEB', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontWeight: 800, color: '#92400E', fontSize: 13, marginBottom: 6 }}>⚠️ This bulletin feeds {linked.length} cost estimate{linked.length > 1 ? 's' : ''}</div>
              <ul style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: 12, color: '#7C5410' }}>
                {linked.map(l => <li key={l.id}>{l.estimate_no || 'Estimate'}{l.gpo_no ? ` — GPO ${l.gpo_no}` : ''}</li>)}
              </ul>
              <div style={{ fontSize: 12, color: '#7C5410', marginBottom: 8 }}>Amending the SAM here will <strong>not</strong> re-cost these estimates automatically. If the price needs to change, update the estimate separately.</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#92400E', cursor: 'pointer' }}>
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} /> I understand — proceed without re-costing.
              </label>
            </div>
          )}

          {/* Header fields */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
            {AMEND_HEADER_FIELDS.map(([k, label]) => (
              <div key={k}>
                <label style={lbl}>{label}</label>
                <input style={{ ...inp, ...(String(header[k] ?? '') !== String(bulletin[k] ?? '') ? { borderColor: '#F59E0B', background: '#FFFBEB' } : {}) }}
                  type={k === 'delivery_date' ? 'date' : (k === 'order_qty' || k === 'sample_qty') ? 'number' : 'text'}
                  value={header[k] ?? ''} onChange={e => setH(k, e.target.value)} />
              </div>
            ))}
          </div>

          {/* Operations / SAM */}
          <div style={{ ...lbl, marginBottom: 6 }}>IE Operations &amp; SAM {opsDirty && <span style={{ color: '#F59E0B' }}>· edited (SAM recomputes on save)</span>}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['#', 'Operation', 'M/C', 'Obs Time (s)', 'Rating %', 'Allow %', 'Basic (s)', 'SAM (min)', 'Prod/Hr', 'Actual Out', 'Eff %'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {ops.map((o, i) => {
                  const c = computed[i];
                  const isBottle = i === bottleneckIdx;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', background: isBottle ? '#fff7ed' : 'white' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, minWidth: 170, textAlign: 'left' }}><input style={inp} value={o.operation_name} onChange={e => setOp(i, { operation_name: e.target.value })} /></td>
                      <td style={{ ...td, width: 70 }}><select style={inp} value={o.machine_type} onChange={e => setOp(i, { machine_type: e.target.value })}>{MACHINE_TYPES.map(m => <option key={m}>{m}</option>)}</select></td>
                      <td style={{ ...td, width: 80 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.observed_time} onChange={e => setOp(i, { observed_time: e.target.value })} /></td>
                      <td style={{ ...td, width: 64 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.rating} onChange={e => setOp(i, { rating: e.target.value })} /></td>
                      <td style={{ ...td, width: 64 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.allowance} onChange={e => setOp(i, { allowance: e.target.value })} /></td>
                      <td style={{ ...td, color: '#666' }}>{c.basic.toFixed(2)}</td>
                      <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{c.sam.toFixed(3)}</td>
                      <td style={{ ...td, color: '#666' }}>{c.prodHr.toFixed(0)}</td>
                      <td style={{ ...td, width: 80 }}><input style={{ ...inp, textAlign: 'center' }} type="number" value={o.actual_output} onChange={e => setOp(i, { actual_output: e.target.value })} /></td>
                      <td style={{ ...td, fontWeight: 600, color: c.eff >= 100 ? '#166534' : c.eff >= 80 ? '#854F0B' : '#A32D2D' }}>{c.eff.toFixed(0)}</td>
                    </tr>
                  );
                })}
                {ops.length === 0 && <tr><td colSpan={11} style={{ ...td, padding: 16, color: '#aaa' }}>No operations recorded.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 20, alignItems: 'baseline' }}>
            <div style={{ fontSize: 13 }}>New Total SAM: <strong style={{ color: '#3B3A8F', fontSize: 16 }}>{totalSam.toFixed(3)}</strong> min</div>
            {origTotal != null && <div style={{ fontSize: 12, color: '#888' }}>was {origTotal.toFixed(3)} min</div>}
          </div>

          {/* Reason (mandatory) */}
          <div style={{ marginTop: 18 }}>
            <label style={lbl}>Reason for amendment <span style={{ color: '#E22D2D' }}>*</span></label>
            <textarea autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. IE entered T&M times in minutes; corrected observed times to seconds (×60)."
              style={{ ...inp, minHeight: 70 }} />
            {!reason.trim() && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>A reason is required to save an amendment.</div>}
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: '#888' }}>{!hasChanges ? 'No changes yet.' : `${Object.keys(headerChanges).length + (opsDirty ? 1 : 0)} change area(s) pending.`}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button type="button" disabled={!canSave} onClick={save}
              style={{ padding: '9px 18px', background: canSave ? '#92400E' : '#d9c4a5', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: canSave ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
              {saving ? 'Saving…' : '✓ Save Amendment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AmendHistoryModal({ bulletin, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/sample-bulletins/${bulletin.id}/amendments`).then(r => setRows(r.data.data || [])).catch(() => setRows([]));
  }, [bulletin.id]);
  const short = (v) => { if (v == null) return '—'; const s = String(v); return s.length > 80 ? s.slice(0, 80) + '…' : s; };
  const th = { padding: '8px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, verticalAlign: 'top', borderBottom: '1px solid #f0f0f0' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1200, overflowY: 'auto', padding: '30px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '94%', maxWidth: 1000, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: '#fff', margin: 0, fontSize: 15 }}>🕑 Amendment History — {bulletin.bulletin_no}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'rgba(255,255,255,0.85)', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 18, maxHeight: '72vh', overflowY: 'auto' }}>
          {rows == null ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</div>
            : rows.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No amendments recorded for this bulletin.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#F7F6F3' }}>{['When', 'By', 'Field', 'Old → New', 'Reason', 'Linked costing warned'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmt(r.amended_at)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.amended_by_name || '—'}</td>
                        <td style={{ ...td, fontWeight: 600, color: '#3B3A8F' }}>{r.field_changed}</td>
                        <td style={td}><span title={String(r.old_value ?? '')} style={{ color: '#A32D2D' }}>{short(r.old_value)}</span> <span style={{ color: '#888' }}>→</span> <span title={String(r.new_value ?? '')} style={{ color: '#166534' }}>{short(r.new_value)}</span></td>
                        <td style={{ ...td, maxWidth: 220 }}>{r.reason}</td>
                        <td style={{ ...td, color: '#92400E' }}>{r.linked_costing_warned || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}

export function TMStudyTab({ user }) {
  const [list, setList] = useState([]);
  const [studying, setStudying] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    // Samples become available for T&M once the Sampling Room marks them ready
    // (sample_ready_tms). 'issued' kept for legacy in-flight bulletins.
    Promise.all([
      api.get('/sample-bulletins?status=sample_ready_tms'),
      api.get('/sample-bulletins?status=tms_in_progress'),
      api.get('/sample-bulletins?status=issued'),
    ])
      .then(([a, b, c]) => setList([...(a.data.data || []), ...(b.data.data || []), ...(c.data.data || [])]))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const acknowledge = async (id) => { try { await api.post(`/sample-bulletins/${id}/acknowledge`); load(); } catch (e) { alert('Failed'); } };

  return (
    <div style={{ padding: 20 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1a1a2e' }}>⏱ Time & Motion Study</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#888' }}>Samples marked “Ready for T&M” by the Sampling Room, awaiting / in study.</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>{['Bulletin No', 'Style', 'Buyer', 'Sample Type', 'Issue Date', 'Operations', 'Status', 'Action'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee' }}>{h}</th>)}</tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>
            : list.length === 0 ? <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No bulletins awaiting T&M study.</td></tr>
              : list.map(b => {
                const opCount = (() => { try { return (typeof b.operations_json === 'string' ? JSON.parse(b.operations_json) : (b.operations_json || [])).filter(o => o.operation_name).length; } catch { return 0; } })();
                return (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: '#3B3A8F' }}>
                      {b.bulletin_no}
                      {(b.media_json || {}).sam_revision && <div title={(b.media_json).sam_revision.comments} style={{ marginTop: 3, fontSize: 10, color: '#9A3412', fontWeight: 600, maxWidth: 180, whiteSpace: 'normal' }}>⚠ SAM revision: {(b.media_json).sam_revision.comments}</div>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{b.style_no || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{b.buyer || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{b.sample_type || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{fmt(b.issue_date)}</td>
                    <td style={{ padding: '8px 10px' }}>{opCount}</td>
                    <td style={{ padding: '8px 10px' }}><span style={{ background: b.status === 'issued' ? '#FAEEDA' : '#EEEDF8', color: b.status === 'issued' ? '#854F0B' : '#2A2970', padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>{SB_STATUS_LABEL[b.status]}</span></td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {(b.status === 'issued' || b.status === 'sample_ready_tms') && <button onClick={() => acknowledge(b.id)} style={actBtn}>Acknowledge Receipt</button>}
                      <button onClick={() => setStudying(b)} style={{ ...actBtn, background: '#16a34a22', color: '#166534' }}>Open T&M Study</button>
                    </td>
                  </tr>
                );
              })}
        </tbody>
      </table>
      <ListFooter shown={list.length} total={list.length} noun="bulletins" />
      {studying && <TMStudyForm bulletin={studying} user={user} onClose={() => setStudying(null)} onSaved={load} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sampling Room (Sampling Supervisor) — drives the physical sample through making,
// T&M readiness, sample photos, and handover to the merchant.
// ════════════════════════════════════════════════════════════════════════════
const SR_SIZES_FALLBACK = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', 'Free Size'];

export function SamplingRoomTab({ user }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [photoFor, setPhotoFor] = useState(null);   // bulletin → ready-for-merchant (photos)
  const [photos, setPhotos] = useState({});
  const [handoverFor, setHandoverFor] = useState(null); // bulletin → hand-to-merchant (checklist)
  const [handover, setHandover] = useState({});
  const [busy, setBusy] = useState(false);

  const SR_STATUSES = ['issued', 'sample_making', 'sample_ready_tms', 'tms_in_progress', 'pending_pm_approval', 'tms_done', 'sample_ready_merchant'];
  const load = () => {
    setLoading(true);
    Promise.all(SR_STATUSES.map(s => api.get('/sample-bulletins?status=' + s)))
      .then(rs => setList(rs.flatMap(r => r.data.data || [])))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const act = async (id, path) => {
    setBusyId(id);
    try { await api.post(`/sample-bulletins/${id}/${path}`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Action failed'); }
    finally { setBusyId(null); }
  };

  const openPhotos = (b) => { setPhotoFor(b); setPhotos((b.media_json || {}).sample_photos || {}); };
  const submitPhotos = async () => {
    setBusy(true);
    try { await api.post(`/sample-bulletins/${photoFor.id}/ready-for-merchant`, { sample_photos: photos }); setPhotoFor(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const openHandover = (b) => { setHandoverFor(b); setHandover({ pieces_count: b.sample_qty || 1, size: '', fabric_swatches: false, trim_card: false, measurement_sheet: false, special_instructions: false, notes: '' }); };
  const submitHandover = async () => {
    setBusy(true);
    try { await api.post(`/sample-bulletins/${handoverFor.id}/hand-to-merchant`, { handover }); setHandoverFor(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const srBtn = (label, onClick, color) => (
    <button type="button" disabled={busyId} onClick={onClick}
      style={{ padding: '5px 12px', background: color, color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: busyId ? 'wait' : 'pointer', marginRight: 6 }}>{label}</button>
  );

  const handoverSizes = (b) => { const s = sbSizesFor(b.division); return s.length ? s : SR_SIZES_FALLBACK; };

  return (
    <div style={{ padding: 20 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1a1a2e' }}>🧵 Sampling Room</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#888' }}>Make the sample, mark it ready for T&M, capture sample photos, and hand over to the merchant.</p>

      {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</div>
        : list.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#aaa', border: '1px dashed #eee', borderRadius: 8 }}>No bulletins in the sampling room right now.</div>
          : (
            <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Bulletin No', 'Style', 'Buyer', 'Sample', 'Issued', 'Status', 'Actions'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
              <tbody>
                {list.map(b => {
                  const lastRej = (b.media_json || {}).last_rejection;
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: '#3B3A8F', whiteSpace: 'nowrap' }}>
                        {b.bulletin_no}
                        {b.revision_no > 1 && <span title={lastRej ? `Rejected: ${lastRej.reason}` : 'Revision'} style={{ marginLeft: 6, background: '#FCEBEB', color: '#A32D2D', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>{sampleOrdinal(b.revision_no)}</span>}
                      </td>
                      <td style={{ padding: '8px 10px' }}>{b.style_no || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{b.buyer || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{b.sample_type || '—'}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmt(b.issue_date)}</td>
                      <td style={{ padding: '8px 10px' }}>{sbStatusChip(b.status)}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {b.status === 'issued' && srBtn('▶ Start Sample Making', () => act(b.id, 'start-sample'), '#9A6B00')}
                        {b.status === 'sample_making' && srBtn('✓ Sample Ready for T&M', () => act(b.id, 'ready-for-tms'), '#2A2970')}
                        {(b.status === 'sample_ready_tms' || b.status === 'tms_in_progress') && <span style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>Awaiting IE T&M study…</span>}
                        {b.status === 'pending_pm_approval' && <span style={{ fontSize: 11, color: '#9A3412', fontStyle: 'italic' }}>Awaiting Production Manager approval…</span>}
                        {b.status === 'tms_done' && srBtn('📷 Sample Ready for Merchant', () => openPhotos(b), '#854F0B')}
                        {b.status === 'sample_ready_merchant' && srBtn('📦 Hand to Merchant', () => openHandover(b), '#16a34a')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ListFooter shown={list.length} total={list.length} noun="bulletins" />
            </>
          )}

      {/* Sample photos modal (front / back / detail) */}
      {photoFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '92%', maxWidth: 560, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#3B3A8F' }}>Sample Photos — {photoFor.bulletin_no}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>Upload front, back & detail shots of the finished sample (JPG/PNG).</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              {[['front', 'Front'], ['back', 'Back'], ['detail', 'Detail']].map(([k, l]) => (
                <div key={k} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6 }}>{l}</div>
                  {photos[k]?.url
                    ? <div style={{ position: 'relative', display: 'inline-block' }}>
                        <a href={photos[k].url} target="_blank" rel="noreferrer"><img src={photos[k].url} alt={l} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} /></a>
                        <button type="button" onClick={() => setPhotos(p => ({ ...p, [k]: null }))} style={{ position: 'absolute', top: -6, right: -6, background: '#E22D2D', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                      </div>
                    : <Uploader accept={IMG_TYPES} label={`📷 ${l}`} onUploaded={m => setPhotos(p => ({ ...p, [k]: m }))} />}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button type="button" onClick={() => setPhotoFor(null)} style={{ padding: '9px 16px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={busy} onClick={submitPhotos} style={{ padding: '9px 16px', background: '#854F0B', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? 'Saving…' : '✓ Mark Sample Ready for Merchant'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Handover checklist modal */}
      {handoverFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, overflowY: 'auto', padding: '20px 0' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '92%', maxWidth: 480, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#16a34a' }}>Handover to Merchant — {handoverFor.bulletin_no}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>Confirm what is being handed over.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={lbl}>Sample Pieces Count</label><input style={inp} type="number" min="1" value={handover.pieces_count} onChange={e => setHandover(h => ({ ...h, pieces_count: e.target.value }))} /></div>
              <div><label style={lbl}>Size</label>
                <select style={inp} value={handover.size} onChange={e => setHandover(h => ({ ...h, size: e.target.value }))}>
                  <option value="">Select size</option>{handoverSizes(handoverFor).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
              {[['fabric_swatches', 'Fabric swatches attached'], ['trim_card', 'Trim card attached'], ['measurement_sheet', 'Measurement sheet attached'], ['special_instructions', 'Special instructions noted']].map(([k, l]) => (
                <label key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13, color: '#444', cursor: 'pointer' }}>
                  <span>{l}</span>
                  <input type="checkbox" checked={!!handover[k]} onChange={e => setHandover(h => ({ ...h, [k]: e.target.checked }))} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                </label>
              ))}
            </div>
            <div style={{ marginTop: 12 }}><label style={lbl}>Notes (optional)</label><textarea style={{ ...inp, minHeight: 50 }} value={handover.notes} onChange={e => setHandover(h => ({ ...h, notes: e.target.value }))} /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button type="button" onClick={() => setHandoverFor(null)} style={{ padding: '9px 16px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={busy || !handover.size} onClick={submitHandover}
                style={{ padding: '9px 16px', background: handover.size ? '#16a34a' : '#9cc7a9', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? 'Saving…' : '📦 Confirm Handover'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Production Manager approval — review T&M results (actual SAMs, total, bottleneck,
// machine requirements) and Approve Production or Reject (request SAM revision).
// ════════════════════════════════════════════════════════════════════════════
const parseOps = (v) => { try { return typeof v === 'string' ? JSON.parse(v) : (v || []); } catch { return []; } };

export function PMApprovalTab({ user }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);   // bulletin under review
  const [comments, setComments] = useState('');
  const [showReject, setShowReject] = useState(false); // reveals the rejection-reason field
  const [busy, setBusy] = useState(false);

  const REJECT_MIN = 10;
  const rejectReady = comments.trim().length >= REJECT_MIN;
  const openReview = (b) => { setReview(b); setComments(''); setShowReject(false); };
  const closeReview = () => { setReview(null); setComments(''); setShowReject(false); };
  // First click reveals the reason field; a second click (once the reason is long
  // enough) actually submits the rejection.
  const onRejectClick = () => { if (!showReject) { setShowReject(true); return; } if (rejectReady) reject(); };

  const load = () => { setLoading(true); api.get('/sample-bulletins?status=pending_pm_approval').then(r => setList(r.data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const ops = review ? parseOps(review.operations_json).filter(o => o.operation_name) : [];
  const totalSam = review ? (review.total_sam_actual != null ? Number(review.total_sam_actual) : ops.reduce((s, o) => s + (parseFloat(o.sam) || 0), 0)) : 0;
  const bottleneck = ops.reduce((b, o) => (parseFloat(o.sam) || 0) > (parseFloat(b?.sam) || 0) ? o : b, null);
  const machineReq = Object.entries(ops.reduce((m, o) => { const k = o.machine_type || '—'; m[k] = (m[k] || 0) + 1; return m; }, {}));

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/sample-bulletins/${review.id}/pm-approve`); closeReview(); load(); }
    catch (e) { alert(e.response?.data?.error || 'Approve failed'); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!rejectReady) return;
    setBusy(true);
    try { await api.post(`/sample-bulletins/${review.id}/pm-reject`, { comments: comments.trim() }); closeReview(); load(); }
    catch (e) { alert(e.response?.data?.error || 'Reject failed'); }
    finally { setBusy(false); }
  };

  const th = { padding: '6px 8px', textAlign: 'left', color: '#888', fontSize: 10, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };
  const card = { background: '#F7F6F3', borderRadius: 8, padding: '12px 14px' };

  return (
    <div style={{ padding: 20 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1a1a2e' }}>✅ Production Approval</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#888' }}>Sample bulletins with a completed T&M study, awaiting Production Manager sign-off.</p>

      {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</div>
        : list.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#aaa', border: '1px dashed #eee', borderRadius: 8 }}>Nothing awaiting Production approval.</div>
          : (
            <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: '#F7F6F3' }}>{['Bulletin No', 'Style', 'Buyer', 'Division', 'Total SAM', 'Bottleneck', 'Status', 'Action'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
              <tbody>
                {list.map(b => {
                  const bops = parseOps(b.operations_json).filter(o => o.operation_name);
                  const bn = bops.reduce((x, o) => (parseFloat(o.sam) || 0) > (parseFloat(x?.sam) || 0) ? o : x, null);
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: '#3B3A8F', whiteSpace: 'nowrap' }}>{b.bulletin_no}</td>
                      <td style={{ padding: '8px 10px' }}>{b.style_no || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{b.buyer || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{b.division || '—'}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: '#3B3A8F' }}>{b.total_sam_actual ? Number(b.total_sam_actual).toFixed(3) : '—'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 11, color: '#E22D2D' }}>{bn?.operation_name || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{sbStatusChip(b.status)}</td>
                      <td style={{ padding: '8px 10px' }}><button onClick={() => openReview(b)} style={{ ...actBtn, background: '#3B3A8F', color: '#fff' }}>Review</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ListFooter shown={list.length} total={list.length} noun="bulletins" />
            </>
          )}

      {/* Review modal */}
      {review && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, overflowY: 'auto', padding: '20px 0' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '95%', maxWidth: 880, boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ background: '#2D2C72', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0' }}>
              <div>
                <h3 style={{ color: 'rgba(255,255,255,0.92)', margin: 0, fontSize: 16 }}>Production Review — {review.bulletin_no}</h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.72)' }}>Style {review.style_no || '—'} · {review.buyer || '—'} · {review.division || '—'}</p>
              </div>
              <button onClick={closeReview} style={{ background: 'none', border: 'none', fontSize: 22, color: 'rgba(255,255,255,0.85)', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
                <div style={card}><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Total SAM</div><div style={{ fontSize: 20, fontWeight: 800, color: '#3B3A8F' }}>{totalSam.toFixed(3)}<span style={{ fontSize: 11, fontWeight: 500 }}> min</span></div></div>
                <div style={card}><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Operations</div><div style={{ fontSize: 20, fontWeight: 800, color: '#3B3A8F' }}>{ops.length}</div></div>
                <div style={card}><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Bottleneck</div><div style={{ fontSize: 13, fontWeight: 700, color: '#E22D2D' }}>{bottleneck?.operation_name || '—'}</div><div style={{ fontSize: 11, color: '#888' }}>{bottleneck ? Number(bottleneck.sam || 0).toFixed(3) + ' min' : ''}</div></div>
                <div style={card}><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Sample Rev</div><div style={{ fontSize: 20, fontWeight: 800, color: '#3B3A8F' }}>{sampleOrdinal(review.revision_no || 1)}</div></div>
              </div>

              {/* Machine requirements */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ ...lbl }}>Machine Requirements</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {machineReq.length === 0 ? <span style={{ fontSize: 12, color: '#aaa' }}>—</span>
                    : machineReq.map(([m, c]) => <span key={m} style={{ background: '#EEEDF8', color: '#2A2970', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>{m} × {c}</span>)}
                </div>
              </div>

              {/* Operations table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ background: '#F7F6F3' }}>{['#', 'Operation', 'Machine', 'Actual SAM (min)', 'Prod/Hr', 'Eff %', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {ops.map((o, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', background: o === bottleneck ? '#fff7ed' : 'white' }}>
                      <td style={{ padding: '5px 8px', color: '#aaa' }}>{i + 1}</td>
                      <td style={{ padding: '5px 8px', fontWeight: 600 }}>{o.operation_name}</td>
                      <td style={{ padding: '5px 8px' }}>{o.machine_type || '—'}</td>
                      <td style={{ padding: '5px 8px', fontWeight: 700, color: '#3B3A8F' }}>{o.sam != null ? Number(o.sam).toFixed(3) : '—'}</td>
                      <td style={{ padding: '5px 8px', color: '#666' }}>{o.prod_per_hr != null ? Number(o.prod_per_hr).toFixed(0) : '—'}</td>
                      <td style={{ padding: '5px 8px', color: '#666' }}>{o.efficiency != null ? Number(o.efficiency).toFixed(0) : '—'}</td>
                      <td style={{ padding: '5px 8px' }}>{o === bottleneck ? <span style={{ fontSize: 10, color: '#E22D2D', fontWeight: 700 }}>🔴 BOTTLE</span> : ''}</td>
                    </tr>
                  ))}
                  {ops.length === 0 && <tr><td colSpan={7} style={{ padding: 18, textAlign: 'center', color: '#aaa' }}>No operations recorded in the T&M study.</td></tr>}
                </tbody>
              </table>
            </div>

            <div style={{ padding: '14px 22px', borderTop: '1px solid #eee' }}>
              {/* Rejection reason — revealed only after the Reject button is clicked. */}
              {showReject && (
                <div style={{ marginBottom: 12 }}>
                  <label style={lbl}>Rejection reason (required — min {REJECT_MIN} characters · SAM revision instructions for IE)</label>
                  <textarea autoFocus value={comments} onChange={e => setComments(e.target.value)} placeholder="e.g. Attach operation SAM looks low, re-time the side-seam operation…"
                    style={{ ...inp, minHeight: 64, border: `1px solid ${rejectReady ? '#16a34a' : '#E22D2D'}` }} />
                  <div style={{ fontSize: 11, marginTop: 4, color: rejectReady ? '#166534' : '#A32D2D' }}>
                    {rejectReady ? '✓ Ready to send for revision' : `${comments.trim().length}/${REJECT_MIN} characters minimum`}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                {/* Reject — always visible & red; clickable to open the reason field, then
                    disabled until the reason reaches the minimum length. */}
                <button type="button" disabled={busy || (showReject && !rejectReady)} onClick={onRejectClick}
                  style={{ flex: 1, padding: '11px 18px', background: '#E22D2D', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: (busy || (showReject && !rejectReady)) ? 'not-allowed' : 'pointer', opacity: (showReject && !rejectReady) ? 0.55 : 1 }}>
                  {busy ? 'Rejecting…' : (showReject ? '✗ Confirm Reject — Send for Revision' : '✗ Reject — Send for Revision')}
                </button>
                <button type="button" disabled={busy} onClick={approve}
                  style={{ flex: 1, padding: '11px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                  {busy ? 'Saving…' : '✓ Approve Production'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
